import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { getCurrentUserId } from 'common-src/features/auth';
import { deleteOrmItem } from 'common-src/features/rest';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import {
  normalizeAppointment,
  syncDashboardV2Appointments,
} from 'common-src/models/DashboardV2Appointment/sync';
import { getDashboardV2Tasks } from 'common-src/models/DashboardV2Task';
import {
  normalizeDashboardV2Task,
  upsertDashboardV2Task,
} from 'common-src/models/DashboardV2Task/sync';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { PatientTaskStatus } from 'common-src/models/PatientTask';
import { updatePatientTask } from 'common-src/models/PatientTask/actions';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import {
  getCareOrchestrationIsLoading,
  getCareOrchestrationMeta,
  setCareOrchestrationError,
  setCareOrchestrationLastFetchedAt,
  setCareOrchestrationLoading,
  setCareOrchestrationMeta,
  setCareOrchestrationStats,
} from 'src/features/careOrchestration';
import {
  getCareOrchestrationState,
  saveCareOrchestrationState,
  saveCareOrchestrationView,
} from 'src/features/tabsState';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useDocumentTitle from 'src/hooks/useDocumentTitle';
import useInterval from 'src/hooks/useInterval';

import styles from './CareOrchestration.module.scss';
import CareOrchestrationHeader from './components/CareOrchestrationHeader';
import ReadinessView from './components/ReadinessView';
import TaskNotificationToasts from './components/TaskNotificationToasts';
import TasksView from './components/TasksView';
import {
  CARE_ORCHESTRATION_BASE_PATH,
  DEFAULT_NOTIFICATION_PREFERENCES,
  DEFAULT_OWNER_STATS,
  DEFAULT_STATS,
  MAX_TASK_NOTIFICATION_TOASTS,
  NOTIFICATION_CATEGORY,
  NOTIFICATION_CATEGORY_KEY_BY_ID,
  NOTIFICATION_STATUS,
  PRIORITY_SORT_ORDER,
  TAB_KEYS,
  TASK_DISPLAY_STATUS,
  TASK_NOTIFICATION_TOAST_STAGGER_MS,
  TASK_NOTIFICATION_TOASTED_IDS_MAX,
  TASK_NOTIFICATION_TOASTED_IDS_STORAGE_KEY,
  TASK_PRIORITY,
  TASK_QUEUE_PAGE_SIZE,
  TASK_QUEUE_SORT_MODE,
} from './constants';
import {
  getAppointmentReadinessStatus,
  getCareOrchestrationPath,
  getTaskBrowserNotificationPermission,
  getTaskDisplayStatus,
  getTaskPatientGroupKey,
  getTaskPatientId,
  groupAppointmentsByDate,
  isAssessmentTask,
  isBrowserNotificationsSupported,
  isEscalationPayloadValid,
  isMessageTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
  isTaskNotificationChannelEnabled,
  matchesTaskStatusFilter,
  normalizeEscalationPayload,
  parseCareOrchestrationRoute,
  playTaskNotificationSound,
  requestTaskBrowserNotificationPermission,
  shouldHideTaskByCallbackStatus,
  showTaskBrowserNotification,
  sortAppointmentsByDate,
  unlockTaskNotificationSound,
} from './helpers';
import {
  assignTaskToMe,
  blockTask,
  deescalateTask,
  delegateSubtasks,
  escalateTask,
  fetchDashboardSupplementalData,
  fetchTaskQueueChanges,
  fetchTaskQueueData,
  fetchTaskQueueItemById,
  fetchTaskSearchPatients,
  fetchTaskSubtasks,
  fetchTaskSubtasksBulk,
  followTask,
  getMyTaskNotificationPreferences,
  getMyTaskNotifications,
  getMyTaskNotificationsStreamUrl,
  markAllMyTaskNotificationsRead,
  markTaskComplete,
  patchMyTaskNotificationPreferences,
  patchMyTaskNotificationStatus,
  reassignTask,
  searchMyTaskNotificationSlackUsers,
  sendMyTaskNotificationTestMessage,
  unassignTask,
  unblockTask,
  unfollowTask,
} from './services/careOrchestrationApi';
import { syncCareOrchestrationData } from './services/syncCareOrchestrationData';

const isTaskFollowStateAlreadyCurrentError = (errorMessage = '') => {
  const normalizedMessage = `${errorMessage}`.trim().toLowerCase();

  return (
    normalizedMessage.includes('task is already followed') ||
    normalizedMessage.includes('task is already unfollowed') ||
    normalizedMessage.includes('task is not being followed')
  );
};

const hasTaskQueueMetaChanged = (previousMeta, nextMeta) =>
  JSON.stringify(previousMeta || null) !== JSON.stringify(nextMeta || null);

const TASK_QUEUE_CHANGES_POLL_INTERVAL_MS = 30000;

const getAppointmentWorkflowKey = (appointmentId, workflowInstanceId = null) =>
  `${appointmentId}::${workflowInstanceId || 'appointment'}`;

const sortReadinessTasks = (tasks = []) =>
  [...tasks].sort((a, b) => {
    const aPriority = a?.priority ?? '';
    const bPriority = b?.priority ?? '';
    const priorityDiff =
      (PRIORITY_SORT_ORDER[aPriority] ?? Number.POSITIVE_INFINITY) -
      (PRIORITY_SORT_ORDER[bPriority] ?? Number.POSITIVE_INFINITY);
    if (priorityDiff !== 0) return priorityDiff;

    const aDue = a?.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
    const bDue = b?.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
    return aDue - bDue;
  });

const normalizeReadinessAppointmentTasksById = (responseBody) => {
  const rawAppointmentTasksById =
    responseBody?.appointmentTasksById ?? responseBody?.appointment_tasks_by_id ?? {};

  return Object.entries(rawAppointmentTasksById).reduce(
    (acc, [lookupKey, appointmentTasks]) => ({
      ...acc,
      [lookupKey]: sortReadinessTasks((appointmentTasks || []).map(normalizeDashboardV2Task)),
    }),
    {},
  );
};

const collectDelegatedSubtaskParentIds = ({
  taskQueueRows = [],
  readinessAppointmentTasksById = {},
}) => {
  const parentTaskIds = new Set();

  const registerTask = (task) => {
    const taskId = `${task?.id || ''}`.trim();
    const parentTaskId = `${task?.parentTaskId || ''}`.trim();

    if (parentTaskId) {
      parentTaskIds.add(parentTaskId);
      return;
    }

    if (taskId) {
      parentTaskIds.add(taskId);
    }
  };

  taskQueueRows.forEach(registerTask);
  Object.values(readinessAppointmentTasksById).flat().forEach(registerTask);

  return Array.from(parentTaskIds);
};

const getAppointmentTaskLookupKey = (appointment) => {
  const sourceAppointmentId = `${appointment?.sourceAppointmentId || ''}`.trim();
  const workflowInstanceId = `${appointment?.workflowInstanceId || ''}`.trim();
  if (sourceAppointmentId && workflowInstanceId) {
    return getAppointmentWorkflowKey(sourceAppointmentId, workflowInstanceId);
  }

  return `${appointment?.id || ''}`.trim();
};

const getTaskWorkflowStageLabel = (task) => {
  const evidenceType = `${task?.evidence?.type || ''}`.trim().toLowerCase();
  const title = `${task?.title || task?.subject || ''}`.trim().toLowerCase();
  const stateName = `${task?.stateName || task?.state_name || ''}`.trim().toLowerCase();

  if (
    evidenceType.includes('post-visit') ||
    title.includes('post-visit') ||
    stateName.includes('post_visit')
  ) {
    return 'Post-visit';
  }

  if (
    evidenceType.includes('pre-visit') ||
    evidenceType.includes('visit execution') ||
    stateName.includes('pre_visit') ||
    stateName.includes('visit_execution')
  ) {
    return 'Pre-visit';
  }

  return 'Workflow';
};

const getWorkflowStageLabel = (workflowTasks = []) => {
  const labels = workflowTasks.map(getTaskWorkflowStageLabel);
  if (labels.includes('Post-visit')) return 'Post-visit';
  if (labels.includes('Pre-visit')) return 'Pre-visit';
  return labels.find(Boolean) || 'Workflow';
};

const isTaskFollowedByCurrentUser = (task, currentUserId) => {
  const normalizedCurrentUserId = `${currentUserId ?? ''}`.trim();
  if (!normalizedCurrentUserId) return false;

  return (task?.followedBy || []).some(
    (followerId) => `${followerId ?? ''}`.trim() === normalizedCurrentUserId,
  );
};

const mergeTaskRowsById = (currentRows = [], nextRows = []) => {
  const mergedRowsById = new Map();

  [...currentRows, ...nextRows].forEach((task) => {
    const taskId = `${task?.id ?? ''}`.trim();
    if (!taskId) return;
    mergedRowsById.set(taskId, task);
  });

  return Array.from(mergedRowsById.values());
};

const getToastedTaskNotificationIds = () => {
  if (typeof window === 'undefined') return new Set();

  try {
    const raw = window.localStorage.getItem(TASK_NOTIFICATION_TOASTED_IDS_STORAGE_KEY);
    if (!raw) return new Set();

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();

    return new Set(parsed);
  } catch {
    return new Set();
  }
};

const saveToastedTaskNotificationIds = (ids) => {
  if (typeof window === 'undefined') return;

  try {
    const normalizedIds = Array.from(ids).slice(-TASK_NOTIFICATION_TOASTED_IDS_MAX);
    window.localStorage.setItem(
      TASK_NOTIFICATION_TOASTED_IDS_STORAGE_KEY,
      JSON.stringify(normalizedIds),
    );
  } catch {
    // Ignore storage failures.
  }
};

export default function MindspanCareOrchestration() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showAlert, AlertType } = useAlert();
  const persistedState = useCustomSelector(getCareOrchestrationState);

  const [ownerFilter, setOwnerFilter] = useState(persistedState?.ownerFilter || 'all');
  const [activeTeam, setActiveTeam] = useState(persistedState?.activeTeam || 'all');
  const [statusFilter, setStatusFilter] = useState(persistedState?.statusFilter || 'open');
  const [patientSearch, setPatientSearch] = useState(persistedState?.patientSearch || null);
  const [taskSortMode, setTaskSortMode] = useState(
    persistedState?.taskSortMode || TASK_QUEUE_SORT_MODE.PatientPriority,
  );
  const [sortKey, setSortKey] = useState(persistedState?.sortKey || 'priority');
  const [sortDirection, setSortDirection] = useState(persistedState?.sortDirection || 'asc');
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [taskFromReadiness, setTaskFromReadiness] = useState(null);
  const [delegatedSubtasksByParentId, setDelegatedSubtasksByParentId] = useState({});
  const [expandedPatients, setExpandedPatients] = useState(persistedState?.expandedPatients || {});
  const [assigningTaskIds, setAssigningTaskIds] = useState(new Set());
  const [unassigningTaskId, setUnassigningTaskId] = useState(null);
  const [markingTaskId, setMarkingTaskId] = useState(null);
  const [reassigningTaskId, setReassigningTaskId] = useState(null);
  const [followingTaskId, setFollowingTaskId] = useState(null);
  const [unfollowingTaskId, setUnfollowingTaskId] = useState(null);
  const [blockingTaskId, setBlockingTaskId] = useState(null);
  const [unblockingTaskId, setUnblockingTaskId] = useState(null);
  const [escalatingTaskId, setEscalatingTaskId] = useState(null);
  const [deescalatingTaskId, setDeescalatingTaskId] = useState(null);
  const [delegatingSubtasksTaskId, setDelegatingSubtasksTaskId] = useState(null);
  const [cancelingSubtaskId, setCancelingSubtaskId] = useState(null);
  const [sessionCompletedTaskIds, setSessionCompletedTaskIds] = useState({});
  const [taskQueueRows, setTaskQueueRows] = useState([]);
  const [taskQueueMeta, setTaskQueueMeta] = useState(null);
  const [readinessSourceAppointments, setReadinessSourceAppointments] = useState([]);
  const [readinessAppointmentTasksById, setReadinessAppointmentTasksById] = useState({});
  const [isLoadingMoreTasks, setIsLoadingMoreTasks] = useState(false);
  const [isExpandingTaskSearch, setIsExpandingTaskSearch] = useState(false);
  const taskQueueRowsRef = useRef([]);
  const taskQueueMetaRef = useRef(null);
  const readinessAppointmentTasksByIdRef = useRef({});
  const delegatedSubtasksByParentIdRef = useRef({});
  const hydratedDelegatedSubtaskParentIdsRef = useRef(new Set());
  const delegatedSubtasksRequestIdRef = useRef(0);
  const ownerFilterRef = useRef(ownerFilter);
  const activeTeamRef = useRef(activeTeam);
  const statusFilterRef = useRef(statusFilter);
  const patientSearchRef = useRef(patientSearch);
  const taskSortModeRef = useRef(taskSortMode);
  const taskQueueCacheRef = useRef({});
  const firstPageTaskQueueByFilterKeyRef = useRef({});
  const taskQueueRequestKeyRef = useRef(null);
  const supplementalDataRef = useRef({
    appointmentsBody: null,
    readinessAppointmentsBody: null,
    statsBody: null,
  });
  const [taskNotificationPreferences, setTaskNotificationPreferences] = useState(null);
  const [isTaskNotificationPreferencesLoading, setIsTaskNotificationPreferencesLoading] =
    useState(true);
  const [isTaskNotificationPreferencesSaving, setIsTaskNotificationPreferencesSaving] =
    useState(false);
  const [taskNotifications, setTaskNotifications] = useState([]);
  const [isTaskNotificationsLoading, setIsTaskNotificationsLoading] = useState(true);
  const [taskNotificationToasts, setTaskNotificationToasts] = useState([]);
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState(
    getTaskBrowserNotificationPermission(),
  );

  const unreadTaskNotificationsCount =
    taskNotificationPreferences?.notificationsEnabled &&
    isTaskNotificationChannelEnabled(taskNotificationPreferences, 'inApp')
      ? taskNotifications.filter((notification) => {
          if (notification?.status !== NOTIFICATION_STATUS.Sent) return false;

          const categoryKey = NOTIFICATION_CATEGORY_KEY_BY_ID[notification?.category];
          if (!categoryKey) return false;

          return !!taskNotificationPreferences?.categories?.[categoryKey]?.inApp;
        }).length
      : 0;

  const careOrchestrationDocumentTitle = unreadTaskNotificationsCount
    ? `(${unreadTaskNotificationsCount}) Orchestration Manager`
    : 'Orchestration Manager';

  const taskNotificationsStreamRef = useRef(null);
  const showAlertRef = useRef(showAlert);
  useEffect(() => {
    showAlertRef.current = showAlert;
  }, [showAlert]);

  const loadTaskNotifications = useCallback(async () => {
    setIsTaskNotificationsLoading(true);

    try {
      const response = await getMyTaskNotifications();

      setTaskNotifications(
        (response || []).filter(
          (notification) =>
            notification?.status !== NOTIFICATION_STATUS.Dismissed &&
            notification?.status !== NOTIFICATION_STATUS.Pending,
        ),
      );
    } catch (error) {
      setTaskNotifications([]);
      showAlertRef.current(
        AlertType.Error,
        '',
        error?.message || 'Failed to load task notifications',
      );
    } finally {
      setIsTaskNotificationsLoading(false);
    }
  }, [AlertType.Error]);

  const getVisibleInAppTaskNotifications = (notifications, preferences) => {
    if (!preferences?.notificationsEnabled) return [];
    if (!isTaskNotificationChannelEnabled(preferences, 'inApp')) return [];

    return (notifications || []).filter((notification) => {
      const categoryKey = NOTIFICATION_CATEGORY_KEY_BY_ID[notification?.category];
      if (!categoryKey) return false;

      return !!preferences?.categories?.[categoryKey]?.inApp;
    });
  };

  const getVisibleBrowserTaskNotifications = (notifications, preferences) => {
    if (!preferences?.notificationsEnabled) return [];
    if (!isTaskNotificationChannelEnabled(preferences, 'browser')) return [];

    return (notifications || []).filter((notification) => {
      const categoryKey = NOTIFICATION_CATEGORY_KEY_BY_ID[notification?.category];
      if (!categoryKey) return false;

      return !!preferences?.categories?.[categoryKey]?.browser;
    });
  };

  const appliedTaskRouteKeyRef = useRef(null);
  const shownTaskNotificationToastIdsRef = useRef(getToastedTaskNotificationIds());
  const shownBrowserNotificationIdsRef = useRef(new Set());
  const lastTaskQueueSyncAtRef = useRef(null);
  const isRefreshingSupplementalDataRef = useRef(false);
  const shouldRefreshSupplementalDataAgainRef = useRef(false);

  const handleTaskNotificationCreated = useCallback(async () => {
    await loadTaskNotifications();
  }, [loadTaskNotifications]);

  const handleTaskNotificationsStreamConnected = useCallback(() => {
    console.info('Task notifications stream connected');
  }, []);

  const handleTaskNotificationsStreamError = useCallback(() => {
    console.error('Task notifications stream error');
  }, []);

  const tasks = useCustomSelector(getDashboardV2Tasks);
  const currentUserId = useCustomSelector(getCurrentUserId);
  const currentUser = useCustomSelector((state) =>
    singleModelSelector(state, 'Client', currentUserId),
  );
  const isCareOrchestrationLoading = useCustomSelector(getCareOrchestrationIsLoading);
  const careOrchestrationMeta = useCustomSelector(getCareOrchestrationMeta);
  const appointments = useCustomSelector(getDashboardV2Appointments);
  const isReadinessEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'careOrchestrationReadiness'),
  );
  const isTasksEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'careOrchestrationTasks'),
  );
  const isStreamSSEEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'taskNotificationsStream'),
  );

  const closeTaskNotificationsStream = useCallback(() => {
    if (taskNotificationsStreamRef.current) {
      taskNotificationsStreamRef.current.removeEventListener(
        'connected',
        handleTaskNotificationsStreamConnected,
      );
      taskNotificationsStreamRef.current.removeEventListener(
        'task-notification-created',
        handleTaskNotificationCreated,
      );
      taskNotificationsStreamRef.current.onerror = null;
      taskNotificationsStreamRef.current.close();
      taskNotificationsStreamRef.current = null;
    }
  }, [handleTaskNotificationCreated, handleTaskNotificationsStreamConnected]);

  const openTaskNotificationsStream = useCallback(() => {
    closeTaskNotificationsStream();
    if (!isStreamSSEEnabled) return;

    const streamUrl = getMyTaskNotificationsStreamUrl();
    const stream = new EventSource(streamUrl, { withCredentials: true });

    stream.addEventListener('connected', handleTaskNotificationsStreamConnected);
    stream.addEventListener('task-notification-created', handleTaskNotificationCreated);
    stream.onerror = handleTaskNotificationsStreamError;

    taskNotificationsStreamRef.current = stream;
  }, [
    closeTaskNotificationsStream,
    handleTaskNotificationCreated,
    handleTaskNotificationsStreamConnected,
    handleTaskNotificationsStreamError,
    isStreamSSEEnabled,
  ]);
  const currentUserName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
  const currentUserNameLower = currentUserName.toLowerCase();

  const syncReadinessAppointmentsState = useCallback((responseBody) => {
    const normalizedAppointments = (responseBody?.data || []).map(normalizeAppointment);
    const normalizedAppointmentTasksById = normalizeReadinessAppointmentTasksById(responseBody);

    setReadinessSourceAppointments(normalizedAppointments);
    setReadinessAppointmentTasksById(normalizedAppointmentTasksById);
    readinessAppointmentTasksByIdRef.current = normalizedAppointmentTasksById;
  }, []);

  const syncLocalReadinessTaskRows = useCallback((updater) => {
    setReadinessAppointmentTasksById((prev) => {
      const nextState = Object.entries(prev).reduce((acc, [lookupKey, appointmentTasks]) => {
        acc[lookupKey] = sortReadinessTasks(
          appointmentTasks.map((taskRow) => {
            if (typeof updater === 'function') {
              return updater(taskRow);
            }

            if (updater?.id && taskRow?.id === updater.id) {
              return {
                ...taskRow,
                ...normalizeDashboardV2Task(updater),
              };
            }

            return taskRow;
          }),
        );
        return acc;
      }, {});

      readinessAppointmentTasksByIdRef.current = nextState;
      return nextState;
    });
  }, []);

  const removeLocalReadinessTaskRows = useCallback((taskIds = []) => {
    const removedTaskIdSet = new Set(
      taskIds.map((taskId) => `${taskId || ''}`.trim()).filter(Boolean),
    );
    if (!removedTaskIdSet.size) return;

    setReadinessAppointmentTasksById((prev) => {
      const nextState = Object.entries(prev).reduce((acc, [lookupKey, appointmentTasks]) => {
        acc[lookupKey] = appointmentTasks.filter(
          (taskRow) => !removedTaskIdSet.has(`${taskRow?.id || ''}`.trim()),
        );
        return acc;
      }, {});

      readinessAppointmentTasksByIdRef.current = nextState;
      return nextState;
    });
  }, []);

  const syncDelegatedSubtasksState = useCallback((updater, hydratedParentTaskIds = []) => {
    setDelegatedSubtasksByParentId((prev) => {
      const nextState = typeof updater === 'function' ? updater(prev) : updater;
      delegatedSubtasksByParentIdRef.current = nextState;

      if (Array.isArray(hydratedParentTaskIds) && hydratedParentTaskIds.length) {
        const nextHydratedParentTaskIds = new Set(hydratedDelegatedSubtaskParentIdsRef.current);
        hydratedParentTaskIds.forEach((taskId) => {
          const normalizedTaskId = `${taskId || ''}`.trim();
          if (normalizedTaskId) {
            nextHydratedParentTaskIds.add(normalizedTaskId);
          }
        });
        hydratedDelegatedSubtaskParentIdsRef.current = nextHydratedParentTaskIds;
      }

      return nextState;
    });
  }, []);

  const hydrateDelegatedSubtasks = useCallback(
    async ({
      taskQueueRows: nextTaskQueueRows = taskQueueRowsRef.current,
      readinessAppointmentTasksById:
        nextReadinessAppointmentTasksById = readinessAppointmentTasksByIdRef.current,
    } = {}) => {
      const parentTaskIds = collectDelegatedSubtaskParentIds({
        taskQueueRows: nextTaskQueueRows,
        readinessAppointmentTasksById: nextReadinessAppointmentTasksById,
      });

      const requestId = delegatedSubtasksRequestIdRef.current + 1;
      delegatedSubtasksRequestIdRef.current = requestId;

      if (!parentTaskIds.length) {
        return;
      }

      const missingParentTaskIds = parentTaskIds.filter(
        (taskId) => !hydratedDelegatedSubtaskParentIdsRef.current.has(taskId),
      );

      if (!missingParentTaskIds.length) {
        return;
      }

      try {
        const subtasksByParentId = await fetchTaskSubtasksBulk(missingParentTaskIds);
        if (delegatedSubtasksRequestIdRef.current !== requestId) return;

        syncDelegatedSubtasksState(
          (prev) => ({
            ...prev,
            ...Object.fromEntries(missingParentTaskIds.map((taskId) => [taskId, []])),
            ...subtasksByParentId,
          }),
          missingParentTaskIds,
        );
      } catch {
        // Ignore subtask hydration failures; a later refresh can reconcile them.
      }
    },
    [syncDelegatedSubtasksState],
  );

  useDocumentTitle(careOrchestrationDocumentTitle);

  const availableTabs = useMemo(
    () => [
      { key: TAB_KEYS.TASKS, enabled: isTasksEnabled },
      { key: TAB_KEYS.READINESS, enabled: isReadinessEnabled },
    ],
    [isReadinessEnabled, isTasksEnabled],
  );

  const firstEnabledTab = useMemo(
    () => availableTabs.find((tab) => tab.enabled)?.key ?? null,
    [availableTabs],
  );

  const hasEnabledTabs = !!firstEnabledTab;
  const requestedRoute = useMemo(
    () => parseCareOrchestrationRoute(location.pathname),
    [location.pathname],
  );
  const requestedView = requestedRoute.view;
  const view = useMemo(() => {
    const enabledViews = availableTabs.filter((tab) => tab.enabled).map((tab) => tab.key);

    if (!enabledViews.length) return null;
    if (requestedView && enabledViews.includes(requestedView)) return requestedView;
    return firstEnabledTab;
  }, [availableTabs, firstEnabledTab, requestedView]);

  const availableTasks = useMemo(
    () =>
      tasks.filter(
        (task) => sessionCompletedTaskIds[task.id] || !shouldHideTaskByCallbackStatus(task),
      ),
    [sessionCompletedTaskIds, tasks],
  );

  const tasksById = useMemo(
    () => availableTasks.reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [availableTasks],
  );
  const allTasksById = useMemo(
    () => tasks.reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [tasks],
  );

  const appointmentTasksById = readinessAppointmentTasksById;
  const readinessAppointments = useMemo(
    () =>
      readinessSourceAppointments.flatMap((appointment) => {
        const appointmentId = `${appointment?.id || ''}`.trim();
        const appointmentTasks = appointmentTasksById[appointmentId] || [];
        if (!appointmentTasks.length) return [appointment];

        const tasksByWorkflowInstanceId = appointmentTasks.reduce((acc, task) => {
          const workflowInstanceId = `${task?.workflowInstanceId || ''}`.trim() || null;
          const workflowKey = workflowInstanceId || 'appointment';
          acc[workflowKey] = acc[workflowKey] || [];
          acc[workflowKey].push(task);
          return acc;
        }, {});

        return Object.entries(tasksByWorkflowInstanceId)
          .map(([workflowKey, workflowTasks]) => {
            const workflowInstanceId = workflowKey === 'appointment' ? null : workflowKey;
            const workflowAppointmentId = getAppointmentWorkflowKey(
              appointmentId,
              workflowInstanceId,
            );
            const workflowStageLabel = getWorkflowStageLabel(workflowTasks);

            return {
              ...(appointment.ref || appointment),
              id: workflowAppointmentId,
              sourceAppointmentId: appointmentId,
              workflowInstanceId: workflowInstanceId || appointment.workflowInstanceId,
              workflowStageLabel,
            };
          })
          .filter((workflowAppointment) => workflowAppointment.workflowStageLabel !== 'Post-visit');
      }),
    [appointmentTasksById, readinessSourceAppointments],
  );
  const appointmentsById = useMemo(
    () =>
      readinessAppointments.reduce(
        (acc, appointment) => ({ ...acc, [appointment.id]: appointment }),
        {},
      ),
    [readinessAppointments],
  );
  const sortedAppts = useMemo(
    () => sortAppointmentsByDate(readinessAppointments),
    [readinessAppointments],
  );
  const apptsByDate = useMemo(() => groupAppointmentsByDate(sortedAppts), [sortedAppts]);
  const appointmentTaskLookupById = useMemo(
    () =>
      [...Object.values(appointmentTasksById), ...Object.values(delegatedSubtasksByParentId)]
        .flat()
        .reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [appointmentTasksById, delegatedSubtasksByParentId],
  );
  useEffect(() => {
    taskQueueRowsRef.current = taskQueueRows;
  }, [taskQueueRows]);

  useEffect(() => {
    taskQueueMetaRef.current = taskQueueMeta;
  }, [taskQueueMeta]);

  useEffect(() => {
    readinessAppointmentTasksByIdRef.current = readinessAppointmentTasksById;
  }, [readinessAppointmentTasksById]);

  useEffect(() => {
    ownerFilterRef.current = ownerFilter;
  }, [ownerFilter]);

  useEffect(() => {
    activeTeamRef.current = activeTeam;
  }, [activeTeam]);

  useEffect(() => {
    statusFilterRef.current = statusFilter;
  }, [statusFilter]);

  useEffect(() => {
    patientSearchRef.current = patientSearch;
  }, [patientSearch]);

  useEffect(() => {
    taskSortModeRef.current = taskSortMode;
  }, [taskSortMode]);

  const getTaskQueueFilterCacheKey = useCallback(
    ({
      sortMode = taskSortModeRef.current,
      team = activeTeamRef.current,
      ownerScope = ownerFilterRef.current,
      statusScope = statusFilterRef.current,
    } = {}) => [sortMode, team, ownerScope, statusScope].join('|'),
    [],
  );

  const shouldHydrateEntireTaskQueue = useCallback(
    (sortMode = taskSortModeRef.current) =>
      sortMode === TASK_QUEUE_SORT_MODE.ReasonPriority ||
      sortMode === TASK_QUEUE_SORT_MODE.Priority,
    [],
  );

  const storeTaskQueueCache = useCallback(
    (cacheKey, nextTaskQueueRows, nextTaskQueueMeta, cacheAsFirstPage = false) => {
      taskQueueCacheRef.current[cacheKey] = {
        taskQueueRows: nextTaskQueueRows,
        taskQueueMeta: nextTaskQueueMeta,
      };

      if (cacheAsFirstPage) {
        firstPageTaskQueueByFilterKeyRef.current[cacheKey] = {
          taskQueueRows: nextTaskQueueRows,
          taskQueueMeta: nextTaskQueueMeta,
        };
      }
    },
    [],
  );

  const invalidateTaskQueueCaches = useCallback(() => {
    taskQueueCacheRef.current = {};
    firstPageTaskQueueByFilterKeyRef.current = {};
    hydratedDelegatedSubtaskParentIdsRef.current = new Set();
  }, []);

  const syncTaskQueueMetaState = useCallback(
    (nextTaskQueueMeta, { invalidateCaches = false } = {}) => {
      if (!nextTaskQueueMeta) return;

      if (invalidateCaches) {
        invalidateTaskQueueCaches();
      }

      taskQueueMetaRef.current = nextTaskQueueMeta;
      setTaskQueueMeta(nextTaskQueueMeta);
      lastTaskQueueSyncAtRef.current =
        nextTaskQueueMeta?.serverTime || lastTaskQueueSyncAtRef.current || new Date().toISOString();
      dispatch(
        setCareOrchestrationMeta({
          tasks: nextTaskQueueMeta,
          appointments:
            careOrchestrationMeta?.appointments ||
            supplementalDataRef.current.appointmentsBody?.meta ||
            null,
          stats:
            careOrchestrationMeta?.stats || supplementalDataRef.current.statsBody?.meta || null,
          readiness:
            careOrchestrationMeta?.readiness ||
            supplementalDataRef.current.readinessAppointmentsBody?.meta ||
            null,
        }),
      );
    },
    [
      careOrchestrationMeta?.appointments,
      careOrchestrationMeta?.readiness,
      careOrchestrationMeta?.stats,
      dispatch,
      invalidateTaskQueueCaches,
    ],
  );

  const syncTaskQueueState = useCallback(
    ({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      appointmentsBody = supplementalDataRef.current.appointmentsBody,
      readinessAppointmentsBody = supplementalDataRef.current.readinessAppointmentsBody,
      statsBody = supplementalDataRef.current.statsBody,
      cacheAsFirstPage = false,
      filterCacheKey = getTaskQueueFilterCacheKey(),
      hydrateSubtasks: shouldHydrateSubtasks = false,
    }) => {
      if (appointmentsBody || readinessAppointmentsBody || statsBody) {
        supplementalDataRef.current = {
          appointmentsBody: appointmentsBody ?? supplementalDataRef.current.appointmentsBody,
          readinessAppointmentsBody:
            readinessAppointmentsBody ?? supplementalDataRef.current.readinessAppointmentsBody,
          statsBody: statsBody ?? supplementalDataRef.current.statsBody,
        };
      }

      storeTaskQueueCache(filterCacheKey, nextTaskQueueRows, nextTaskQueueMeta, cacheAsFirstPage);

      setTaskQueueRows(nextTaskQueueRows);
      setTaskQueueMeta(nextTaskQueueMeta);
      lastTaskQueueSyncAtRef.current = nextTaskQueueMeta?.serverTime || new Date().toISOString();

      syncCareOrchestrationData({
        dispatch,
        taskQueueRows: nextTaskQueueRows,
        taskQueueMeta: nextTaskQueueMeta,
        appointmentsBody: supplementalDataRef.current.appointmentsBody,
        statsBody: supplementalDataRef.current.statsBody,
      });
      if (supplementalDataRef.current.readinessAppointmentsBody) {
        syncReadinessAppointmentsState(supplementalDataRef.current.readinessAppointmentsBody);
      }

      if (shouldHydrateSubtasks) {
        hydrateDelegatedSubtasks({
          taskQueueRows: nextTaskQueueRows,
          readinessAppointmentTasksById: supplementalDataRef.current.readinessAppointmentsBody
            ? normalizeReadinessAppointmentTasksById(
                supplementalDataRef.current.readinessAppointmentsBody,
              )
            : readinessAppointmentTasksByIdRef.current,
        });
      }
    },
    [
      dispatch,
      getTaskQueueFilterCacheKey,
      hydrateDelegatedSubtasks,
      storeTaskQueueCache,
      syncReadinessAppointmentsState,
    ],
  );

  const fetchTaskQueueSnapshot = useCallback(
    async ({
      pagesToLoad = 1,
      sortMode = taskSortModeRef.current,
      team = activeTeamRef.current,
      ownerScope = ownerFilterRef.current,
      statusScope = statusFilterRef.current,
      loadAllPages = false,
      includeSupplemental = false,
    } = {}) => {
      const accumulatedRows = [];
      let latestTaskQueueMeta = null;
      let pageIndex = 0;
      let shouldContinue = true;

      /* eslint-disable no-await-in-loop */
      while (shouldContinue) {
        const { taskQueueRows: nextPageRows, taskQueueMeta: nextPageMeta } =
          await fetchTaskQueueData({
            pageSize: TASK_QUEUE_PAGE_SIZE,
            offset: pageIndex * TASK_QUEUE_PAGE_SIZE,
            sortMode,
            team,
            ownerScope,
            statusScope,
          });

        latestTaskQueueMeta = nextPageMeta;
        accumulatedRows.push(...nextPageRows);

        const total = nextPageMeta?.total ?? accumulatedRows.length;
        const resolvedPagesToLoad = Math.max(1, pagesToLoad);
        const hasLoadedRequestedPages = !loadAllPages && pageIndex + 1 >= resolvedPagesToLoad;

        if (!nextPageRows.length || accumulatedRows.length >= total || hasLoadedRequestedPages) {
          shouldContinue = false;
          continue;
        }

        pageIndex += 1;
      }
      /* eslint-enable no-await-in-loop */

      let supplementalData = {};
      if (includeSupplemental) {
        supplementalData = await fetchDashboardSupplementalData();
      }

      return {
        taskQueueRows: accumulatedRows,
        taskQueueMeta: latestTaskQueueMeta,
        ...supplementalData,
      };
    },
    [],
  );

  const refreshCareOrchestrationData = useCallback(async () => {
    const loadedCount = Math.max(taskQueueRowsRef.current.length, TASK_QUEUE_PAGE_SIZE);
    const pagesToLoad = Math.max(1, Math.ceil(loadedCount / TASK_QUEUE_PAGE_SIZE));
    const {
      taskQueueRows: nextTaskQueueRows,
      taskQueueMeta: nextTaskQueueMeta,
      appointmentsBody,
      readinessAppointmentsBody,
      statsBody,
    } = await fetchTaskQueueSnapshot({
      pagesToLoad,
      sortMode: taskSortModeRef.current,
      team: activeTeamRef.current,
      ownerScope: ownerFilterRef.current,
      statusScope: statusFilterRef.current,
      includeSupplemental: true,
    });

    syncTaskQueueState({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      appointmentsBody,
      readinessAppointmentsBody,
      statsBody,
      hydrateSubtasks: true,
    });
  }, [fetchTaskQueueSnapshot, syncTaskQueueState]);

  const refreshTaskQueueData = useCallback(async () => {
    const loadedCount = Math.max(taskQueueRowsRef.current.length, TASK_QUEUE_PAGE_SIZE);
    const pagesToLoad = Math.max(1, Math.ceil(loadedCount / TASK_QUEUE_PAGE_SIZE));
    const { taskQueueRows: nextTaskQueueRows, taskQueueMeta: nextTaskQueueMeta } =
      await fetchTaskQueueSnapshot({
        pagesToLoad,
        sortMode: taskSortModeRef.current,
        team: activeTeamRef.current,
        ownerScope: ownerFilterRef.current,
        statusScope: statusFilterRef.current,
      });

    syncTaskQueueState({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      hydrateSubtasks: true,
    });
  }, [fetchTaskQueueSnapshot, syncTaskQueueState]);

  const loadMoreTaskQueueData = useCallback(async () => {
    if (isLoadingMoreTasks) return false;

    const loadedRows = taskQueueRowsRef.current;
    const currentMeta = taskQueueMetaRef.current;
    const total = currentMeta?.total ?? loadedRows.length;
    if (loadedRows.length >= total) return false;

    setIsLoadingMoreTasks(true);

    try {
      const { taskQueueRows: nextPageRows, taskQueueMeta: nextPageMeta } = await fetchTaskQueueData(
        {
          pageSize: TASK_QUEUE_PAGE_SIZE,
          offset: loadedRows.length,
          sortMode: taskSortModeRef.current,
          team: activeTeamRef.current,
          ownerScope: ownerFilterRef.current,
          statusScope: statusFilterRef.current,
        },
      );

      syncTaskQueueState({
        nextTaskQueueRows: [...loadedRows, ...nextPageRows],
        nextTaskQueueMeta: nextPageMeta,
        hydrateSubtasks: true,
      });

      return nextPageRows.length > 0;
    } finally {
      setIsLoadingMoreTasks(false);
    }
  }, [isLoadingMoreTasks, syncTaskQueueState]);

  const hasMoreTasks = (taskQueueMeta?.total ?? taskQueueRows.length) > taskQueueRows.length;

  const expandTaskSearchScope = useCallback(async () => {
    const currentMeta = taskQueueMetaRef.current;
    const currentRows = taskQueueRowsRef.current;
    const total = currentMeta?.total ?? currentRows.length;

    if (!total || currentRows.length >= total || isExpandingTaskSearch) {
      return currentRows;
    }

    setIsExpandingTaskSearch(true);

    try {
      const pagesToLoad = Math.max(1, Math.ceil(total / TASK_QUEUE_PAGE_SIZE));
      const { taskQueueRows: nextTaskQueueRows, taskQueueMeta: nextTaskQueueMeta } =
        await fetchTaskQueueSnapshot({
          pagesToLoad,
          sortMode: taskSortModeRef.current,
          team: activeTeamRef.current,
          ownerScope: ownerFilterRef.current,
          statusScope: statusFilterRef.current,
        });

      syncTaskQueueState({
        nextTaskQueueRows,
        nextTaskQueueMeta,
        hydrateSubtasks: true,
      });

      return nextTaskQueueRows;
    } finally {
      setIsExpandingTaskSearch(false);
    }
  }, [fetchTaskQueueSnapshot, isExpandingTaskSearch, syncTaskQueueState]);

  const resetTaskSearchScope = useCallback(async () => {
    const cacheKey = getTaskQueueFilterCacheKey();
    const cachedFirstPage = firstPageTaskQueueByFilterKeyRef.current[cacheKey];

    if (cachedFirstPage) {
      syncTaskQueueState({
        nextTaskQueueRows: cachedFirstPage.taskQueueRows,
        nextTaskQueueMeta: cachedFirstPage.taskQueueMeta,
        hydrateSubtasks: true,
      });
      return;
    }

    const { taskQueueRows: nextTaskQueueRows, taskQueueMeta: nextTaskQueueMeta } =
      await fetchTaskQueueSnapshot({
        pagesToLoad: 1,
        sortMode: taskSortModeRef.current,
        team: activeTeamRef.current,
        ownerScope: ownerFilterRef.current,
        statusScope: statusFilterRef.current,
        loadAllPages: shouldHydrateEntireTaskQueue(taskSortModeRef.current),
      });

    syncTaskQueueState({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      cacheAsFirstPage: true,
      hydrateSubtasks: true,
    });
  }, [
    fetchTaskQueueSnapshot,
    getTaskQueueFilterCacheKey,
    shouldHydrateEntireTaskQueue,
    syncTaskQueueState,
  ]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const cacheKey = getTaskQueueFilterCacheKey({
        sortMode: taskSortMode,
        team: activeTeam,
        ownerScope: ownerFilter,
        statusScope: statusFilter,
      });
      const cachedTaskQueue = taskQueueCacheRef.current[cacheKey];

      if (taskQueueRequestKeyRef.current === cacheKey) {
        return;
      }

      dispatch(setCareOrchestrationLoading(true));
      dispatch(setCareOrchestrationError(null));

      try {
        if (cachedTaskQueue) {
          if (cancelled) return;

          syncTaskQueueState({
            nextTaskQueueRows: cachedTaskQueue.taskQueueRows,
            nextTaskQueueMeta: cachedTaskQueue.taskQueueMeta,
            filterCacheKey: cacheKey,
            hydrateSubtasks: true,
          });
          return;
        }

        taskQueueRequestKeyRef.current = cacheKey;
        const shouldIncludeSupplemental = view === TAB_KEYS.READINESS;

        const {
          taskQueueRows: nextTaskQueueRows,
          taskQueueMeta: nextTaskQueueMeta,
          appointmentsBody,
          readinessAppointmentsBody,
          statsBody,
        } = await fetchTaskQueueSnapshot({
          pagesToLoad: 1,
          sortMode: taskSortMode,
          team: activeTeam,
          ownerScope: ownerFilter,
          statusScope: statusFilter,
          loadAllPages: shouldHydrateEntireTaskQueue(taskSortMode),
          includeSupplemental: shouldIncludeSupplemental,
        });

        if (cancelled) return;

        const latestCacheKey = getTaskQueueFilterCacheKey({
          sortMode: taskSortModeRef.current,
          team: activeTeamRef.current,
          ownerScope: ownerFilterRef.current,
          statusScope: statusFilterRef.current,
        });

        if (latestCacheKey !== cacheKey) {
          storeTaskQueueCache(cacheKey, nextTaskQueueRows, nextTaskQueueMeta, true);
          return;
        }

        syncTaskQueueState({
          nextTaskQueueRows,
          nextTaskQueueMeta,
          appointmentsBody,
          readinessAppointmentsBody,
          statsBody,
          cacheAsFirstPage: true,
          filterCacheKey: cacheKey,
          hydrateSubtasks: true,
        });
      } catch (error) {
        if (cancelled) return;

        const errorMessage = error?.message || 'Failed to load care orchestration data';
        dispatch(setCareOrchestrationError(errorMessage));
        showAlert(AlertType.Error, '', errorMessage);
      } finally {
        if (taskQueueRequestKeyRef.current === cacheKey) {
          taskQueueRequestKeyRef.current = null;
        }
        if (!cancelled) {
          dispatch(setCareOrchestrationLoading(false));
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    activeTeam,
    dispatch,
    fetchTaskQueueSnapshot,
    getTaskQueueFilterCacheKey,
    ownerFilter,
    statusFilter,
    shouldHydrateEntireTaskQueue,
    storeTaskQueueCache,
    syncTaskQueueState,
    taskSortMode,
    view,
  ]);

  const syncLocalTaskRows = useCallback(
    (updater) => {
      tasks.forEach((taskRow) => {
        let nextTaskRow = taskRow;

        if (typeof updater === 'function') {
          nextTaskRow = updater(taskRow);
        } else if (updater?.id && taskRow?.id === updater.id) {
          nextTaskRow = { ...taskRow, ...updater };
        }

        if (nextTaskRow && nextTaskRow !== taskRow) {
          upsertDashboardV2Task(nextTaskRow);
        }
      });

      syncLocalReadinessTaskRows((taskRow) => {
        if (typeof updater === 'function') {
          return updater(taskRow);
        }

        if (updater?.id && taskRow?.id === updater.id) {
          return {
            ...taskRow,
            ...normalizeDashboardV2Task(updater),
          };
        }

        return taskRow;
      });
    },
    [syncLocalReadinessTaskRows, tasks],
  );

  const upsertTaskFromResponse = useCallback(
    (taskRow) => {
      if (!taskRow?.id) return;
      upsertDashboardV2Task(taskRow);
      syncLocalReadinessTaskRows(taskRow);
    },
    [syncLocalReadinessTaskRows],
  );

  const refreshSupplementalData = useCallback(async () => {
    if (isRefreshingSupplementalDataRef.current) {
      shouldRefreshSupplementalDataAgainRef.current = true;
      return;
    }

    isRefreshingSupplementalDataRef.current = true;

    try {
      const { appointmentsBody, readinessAppointmentsBody, statsBody } =
        await fetchDashboardSupplementalData();

      syncDashboardV2Appointments(appointmentsBody);
      syncReadinessAppointmentsState(readinessAppointmentsBody);
      dispatch(setCareOrchestrationStats(statsBody?.data || null));
      dispatch(
        setCareOrchestrationMeta({
          tasks: careOrchestrationMeta?.tasks || null,
          appointments: appointmentsBody?.meta || null,
          stats: statsBody?.meta || null,
          readiness: careOrchestrationMeta?.readiness || null,
        }),
      );
      supplementalDataRef.current = {
        appointmentsBody,
        readinessAppointmentsBody,
        statsBody,
      };
      dispatch(setCareOrchestrationLastFetchedAt(new Date().toISOString()));
    } finally {
      isRefreshingSupplementalDataRef.current = false;

      if (shouldRefreshSupplementalDataAgainRef.current) {
        shouldRefreshSupplementalDataAgainRef.current = false;
        refreshSupplementalData();
      }
    }
  }, [
    careOrchestrationMeta?.readiness,
    careOrchestrationMeta?.tasks,
    dispatch,
    syncReadinessAppointmentsState,
  ]);

  useEffect(() => {
    if (view !== TAB_KEYS.READINESS) return;

    const hasSupplementalData =
      !!supplementalDataRef.current.appointmentsBody &&
      !!supplementalDataRef.current.readinessAppointmentsBody &&
      !!supplementalDataRef.current.statsBody;

    if (hasSupplementalData) return;

    refreshSupplementalData().catch(() => null);
  }, [refreshSupplementalData, view]);

  const applyTaskQueueChanges = useCallback(
    (changesBody, { invalidateCaches = false } = {}) => {
      const changedTaskRows = changesBody?.data || [];
      const changedSubtasksByParentId = changesBody?.changedSubtasksByParentId || {};

      changedTaskRows.forEach((taskRow) => {
        if (taskRow?.id) {
          upsertDashboardV2Task(taskRow);
          syncLocalReadinessTaskRows(taskRow);
        }
      });

      const removedTaskIds = changesBody?.removedTaskIds || [];
      removedTaskIds.forEach((taskId) => {
        if (taskId) {
          deleteOrmItem('DashboardV2Task', taskId);
        }
      });
      removeLocalReadinessTaskRows(removedTaskIds);

      const knownAppointmentIds = new Set(
        appointments.map((appointment) => `${appointment?.id || ''}`.trim()).filter(Boolean),
      );
      const knownReadinessAppointmentIds = new Set(
        readinessSourceAppointments
          .map((appointment) =>
            `${appointment?.sourceAppointmentId || appointment?.id || ''}`.trim(),
          )
          .filter(Boolean),
      );
      const hasTaskForNewAppointment = changedTaskRows.some((taskRow) => {
        const appointmentId = `${taskRow?.externalObjectId || ''}`.trim();
        return appointmentId && !knownAppointmentIds.has(appointmentId);
      });
      const hasTaskForTrackedReadinessAppointment =
        removedTaskIds.some((taskId) => !!appointmentTaskLookupById[taskId]) ||
        changedTaskRows.some((taskRow) => {
          const appointmentId = `${taskRow?.externalObjectId || ''}`.trim();
          return appointmentId && knownReadinessAppointmentIds.has(appointmentId);
        });

      const shouldRefreshReadinessSupplementalData =
        removedTaskIds.length > 0 ||
        hasTaskForNewAppointment ||
        hasTaskForTrackedReadinessAppointment;

      if (shouldRefreshReadinessSupplementalData) {
        refreshSupplementalData().catch(() => null);
      }

      if (Object.keys(changedSubtasksByParentId).length > 0) {
        syncDelegatedSubtasksState(
          (prev) => ({
            ...prev,
            ...changedSubtasksByParentId,
          }),
          Object.keys(changedSubtasksByParentId),
        );
      }

      const nextTaskQueueMeta = changesBody?.meta || null;
      if (nextTaskQueueMeta) {
        const shouldInvalidateCaches =
          invalidateCaches ||
          changedTaskRows.length > 0 ||
          removedTaskIds.length > 0 ||
          Object.keys(changedSubtasksByParentId).length > 0 ||
          hasTaskQueueMetaChanged(taskQueueMetaRef.current, nextTaskQueueMeta);
        syncTaskQueueMetaState(nextTaskQueueMeta, { invalidateCaches: shouldInvalidateCaches });
      }

      if (changesBody?.serverTime) {
        lastTaskQueueSyncAtRef.current = changesBody.serverTime;
      }
    },
    [
      appointmentTaskLookupById,
      appointments,
      readinessSourceAppointments,
      refreshSupplementalData,
      removeLocalReadinessTaskRows,
      syncTaskQueueMetaState,
      syncLocalReadinessTaskRows,
    ],
  );

  const refreshCareOrchestrationDataSafely = useCallback(
    async () => refreshCareOrchestrationData().catch(() => null),
    [],
  );

  const refreshTaskQueueChanges = useCallback(
    async ({ invalidateCaches = false } = {}) => {
      const since = lastTaskQueueSyncAtRef.current;
      if (!since) {
        await refreshTaskQueueData();
        return false;
      }

      const changesBody = await fetchTaskQueueChanges(since, {
        team: activeTeamRef.current,
        ownerScope: ownerFilterRef.current,
        statusScope: statusFilterRef.current,
        mrn: patientSearchRef.current?.mrn || null,
      });

      applyTaskQueueChanges(changesBody, { invalidateCaches });
      return true;
    },
    [applyTaskQueueChanges, refreshTaskQueueData],
  );

  const refreshTaskQueueDataSafely = useCallback(
    async () => refreshTaskQueueData().catch(() => null),
    [],
  );

  const refreshTaskQueueChangesSafely = useCallback(
    async ({ invalidateCaches = false } = {}) => {
      try {
        await refreshTaskQueueChanges({ invalidateCaches });
      } catch {
        await refreshTaskQueueDataSafely();
      }
    },
    [refreshTaskQueueChanges, refreshTaskQueueDataSafely],
  );

  useEffect(() => {
    if (!hasEnabledTabs) return undefined;

    const pollTaskQueueChanges = async () => {
      try {
        await refreshTaskQueueChanges();
      } catch {
        // Ignore polling failures; the next interval or full refresh will reconcile state.
      }
    };

    const intervalId = window.setInterval(
      pollTaskQueueChanges,
      TASK_QUEUE_CHANGES_POLL_INTERVAL_MS,
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pollTaskQueueChanges();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasEnabledTabs, refreshTaskQueueChanges]);

  const handleAssignToMe = async (taskId) => {
    if (!taskId || assigningTaskIds.has(taskId)) return;

    setAssigningTaskIds((prev) => new Set(prev).add(taskId));

    const task = tasksById[taskId] ?? allTasksById[taskId] ?? appointmentTaskLookupById[taskId];
    const wasFollowingTask = isTaskFollowedByCurrentUser(task, currentUserId);

    try {
      const updatedTask = await assignTaskToMe(taskId);
      let autoUnfollowWarning = null;

      if (wasFollowingTask) {
        try {
          await unfollowTask(taskId);
        } catch (error) {
          const errorMessage = error?.message || 'Failed to unfollow task';

          if (!isTaskFollowStateAlreadyCurrentError(errorMessage)) {
            autoUnfollowWarning = errorMessage;
          }
        }
      }

      upsertTaskFromResponse({
        ...(updatedTask || task),
        isFollowing: false,
        followedBy: (updatedTask?.followedBy || task?.followedBy || []).filter(
          (followerId) => `${followerId ?? ''}`.trim() !== `${currentUserId ?? ''}`.trim(),
        ),
      });
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });

      if (autoUnfollowWarning) {
        showAlert(
          AlertType.Warning,
          '',
          `Task claimed, but automatic unfollow failed: ${autoUnfollowWarning}`,
        );
      }

      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to assign task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setAssigningTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  const handleMarkComplete = async (taskId) => {
    if (!taskId || markingTaskId) return;

    setMarkingTaskId(taskId);
    const taskToComplete =
      tasksById[taskId] ?? allTasksById[taskId] ?? appointmentTaskLookupById[taskId] ?? null;
    const parentTaskId = `${taskToComplete?.parentTaskId || ''}`.trim();
    const delegatedSubtasksParentId = parentTaskId || `${taskId}`.trim();

    try {
      const updatedTask = await markTaskComplete(taskId);
      upsertTaskFromResponse(updatedTask);
      setSessionCompletedTaskIds((prev) => ({ ...prev, [taskId]: true }));

      const refreshedSubtasks = delegatedSubtasksParentId
        ? await fetchTaskSubtasks(delegatedSubtasksParentId)
        : [];

      if (Array.isArray(refreshedSubtasks)) {
        refreshedSubtasks.forEach((subtask) => {
          upsertTaskFromResponse(subtask);
        });

        if (delegatedSubtasksParentId) {
          syncDelegatedSubtasksState(
            (prev) => ({
              ...prev,
              [delegatedSubtasksParentId]: refreshedSubtasks,
            }),
            [delegatedSubtasksParentId],
          );
        }
      }

      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      showAlert(AlertType.Success, '', 'Task successfully marked as completed.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to mark task complete';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setMarkingTaskId(null);
    }
  };

  const handleUnassignTask = async (taskId) => {
    if (!taskId || unassigningTaskId) return false;

    setUnassigningTaskId(taskId);

    try {
      const updatedTask = await unassignTask(taskId);
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unassign task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnassigningTaskId(null);
    }
  };

  const handleFollowTask = async (taskId) => {
    if (!taskId || followingTaskId) return false;

    setFollowingTaskId(taskId);

    try {
      await followTask(taskId);
      showAlert(AlertType.Success, '', 'Successfully following task.');
      syncLocalTaskRows((taskRow) => {
        if (taskRow?.id !== taskId) return taskRow;

        const nextFollowedBy = Array.from(
          new Set([...(taskRow?.followedBy || []), currentUserId].filter(Boolean)),
        );

        return {
          ...taskRow,
          isFollowing: true,
          followedBy: nextFollowedBy,
        };
      });
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to follow task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setFollowingTaskId(null);
    }
  };

  const handleUnfollowTask = async (taskId) => {
    if (!taskId || unfollowingTaskId) return false;

    setUnfollowingTaskId(taskId);

    try {
      await unfollowTask(taskId);
      showAlert(AlertType.Success, '', 'Successfully unfollowed task.');
      syncLocalTaskRows((taskRow) => {
        if (taskRow?.id !== taskId) return taskRow;

        return {
          ...taskRow,
          isFollowing: false,
          followedBy: (taskRow?.followedBy || []).filter(
            (followerId) => `${followerId ?? ''}`.trim() !== `${currentUserId ?? ''}`.trim(),
          ),
        };
      });
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unfollow task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnfollowingTaskId(null);
    }
  };

  const handleReassignTask = async (taskId, assigneeId) => {
    const normalizedAssigneeId = Number(assigneeId);
    if (!taskId || !Number.isInteger(normalizedAssigneeId) || reassigningTaskId) return false;

    setReassigningTaskId(taskId);

    try {
      const updatedTask = await reassignTask(taskId, normalizedAssigneeId);
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to reassign task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setReassigningTaskId(null);
    }
  };

  const handleBlockTask = async (taskId, blockedReason) => {
    if (!taskId || blockedReason == null || blockingTaskId) return false;

    setBlockingTaskId(taskId);

    try {
      const updatedTask = await blockTask(taskId, blockedReason);
      showAlert(AlertType.Success, '', 'Task successfully blocked.');
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to block task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setBlockingTaskId(null);
    }
  };

  const handleUnblockTask = async (taskId) => {
    if (!taskId || unblockingTaskId) return false;

    setUnblockingTaskId(taskId);

    try {
      const updatedTask = await unblockTask(taskId);
      showAlert(AlertType.Success, '', 'Task successfully unblocked.');
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unblock task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnblockingTaskId(null);
    }
  };

  const handleEscalateTask = async (taskId, escalationPayload = {}) => {
    const normalizedPayload = normalizeEscalationPayload(escalationPayload);

    if (!taskId || escalatingTaskId || !isEscalationPayloadValid(normalizedPayload)) {
      return false;
    }

    setEscalatingTaskId(taskId);

    try {
      await escalateTask(taskId, normalizedPayload);

      let lastUpdatedTask;
      if (Number.isInteger(normalizedPayload.escalatedToUser)) {
        lastUpdatedTask = await reassignTask(taskId, normalizedPayload.escalatedToUser);
      } else {
        lastUpdatedTask = await unassignTask(taskId);
      }

      upsertTaskFromResponse(lastUpdatedTask);
      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      showAlert(AlertType.Success, '', 'Task successfully escalated.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to escalate task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setEscalatingTaskId(null);
    }
  };

  const handleDeescalateTask = async (taskId) => {
    if (!taskId || deescalatingTaskId) return false;

    setDeescalatingTaskId(taskId);

    try {
      const updatedTask = await deescalateTask(taskId);

      const previousAssigneeIdValue =
        tasksById[taskId]?.previousAssigneeId ?? updatedTask?.previousAssigneeId;
      const previousAssigneeId =
        previousAssigneeIdValue == null ? null : Number(previousAssigneeIdValue);
      if (Number.isInteger(previousAssigneeId) && previousAssigneeId > 0) {
        const reassignedTask = await reassignTask(taskId, previousAssigneeId);
        upsertTaskFromResponse(reassignedTask);
      } else {
        const unassignedTask = await unassignTask(taskId);
        upsertTaskFromResponse(unassignedTask);
      }

      await refreshTaskQueueChangesSafely({ invalidateCaches: true });
      showAlert(AlertType.Success, '', 'Task successfully de-escalated.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to de-escalate task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setDeescalatingTaskId(null);
    }
  };

  const handleDelegateSubtasks = async (task, subtasks = []) => {
    const taskId = task?.id;
    const patientId = Number(getTaskPatientId(task) ?? task?.patientId ?? task?.patient?.id);
    const typeId = Number(task?.typeId);
    const reasonId = Number(task?.reasonId);

    if (
      !taskId ||
      delegatingSubtasksTaskId ||
      !Number.isInteger(patientId) ||
      !Number.isInteger(typeId) ||
      !Number.isInteger(reasonId) ||
      !Array.isArray(subtasks) ||
      !subtasks.length
    ) {
      return false;
    }

    const tasksPayload = subtasks
      .map((subtask) => {
        const subject = `${subtask?.title || ''}`.trim();
        const description = `${subtask?.note || ''}`.trim();
        const team = `${subtask?.team || ''}`.trim().toLowerCase();
        const assigneeId = Number(subtask?.assignee?.userId);
        const hasDirectAssignee =
          subtask?.assignee?.type === 'user' && Number.isInteger(assigneeId);
        const priority = `${subtask?.priority || ''}`.trim().toLowerCase();
        const due = `${subtask?.due || ''}`.trim();

        if (!subject || (!team && !hasDirectAssignee)) return null;

        return {
          patientId,
          subject,
          typeId,
          reasonId,
          description,
          priority,
          taskDueAt: due ? new Date(`${due}T00:00:00Z`).toISOString() : null,
          parentTaskId: taskId,
          ...(hasDirectAssignee ? { assigneeId } : { team }),
        };
      })
      .filter(Boolean);

    if (!tasksPayload.length || tasksPayload.length !== subtasks.length) {
      return false;
    }

    setDelegatingSubtasksTaskId(taskId);

    try {
      await delegateSubtasks({ tasks: tasksPayload });
      await refreshTaskQueueDataSafely();
      showAlert(AlertType.Success, '', 'Sub-tasks successfully created.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to create sub-tasks';
      await refreshTaskQueueDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setDelegatingSubtasksTaskId(null);
    }
  };

  const handleCancelSubtask = async (taskId) => {
    if (!taskId || cancelingSubtaskId) return false;

    setCancelingSubtaskId(taskId);
    const subtask =
      tasksById[taskId] ?? allTasksById[taskId] ?? appointmentTaskLookupById[taskId] ?? null;
    const parentTaskId = `${subtask?.parentTaskId || ''}`.trim();

    try {
      await dispatch(updatePatientTask(taskId, { status: PatientTaskStatus.Canceled.value }));

      const canceledSubtask = {
        ...(subtask || {}),
        id: taskId,
        status: PatientTaskStatus.Canceled.value,
      };

      upsertTaskFromResponse(canceledSubtask);

      if (parentTaskId) {
        syncDelegatedSubtasksState(
          (prev) => ({
            ...prev,
            [parentTaskId]: (prev[parentTaskId] || []).map((childTask) =>
              `${childTask?.id || ''}`.trim() === `${taskId}`
                ? {
                    ...childTask,
                    status: PatientTaskStatus.Canceled.value,
                  }
                : childTask,
            ),
          }),
          [parentTaskId],
        );
      }

      showAlert(AlertType.Success, '', 'Sub-task successfully canceled.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to cancel sub-task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setCancelingSubtaskId(null);
    }
  };

  useEffect(() => {
    if (selectedTask?.id && !selectedTask?.parentTaskId && !allTasksById[selectedTask.id]) {
      setSelectedTask(null);
      dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
    }
    if (taskFromReadiness?.id && !appointmentTaskLookupById[taskFromReadiness.id]) {
      setTaskFromReadiness(null);
      dispatch(saveCareOrchestrationState({ taskFromReadinessId: null }));
    }
  }, [allTasksById, appointmentTaskLookupById, dispatch, selectedTask, taskFromReadiness]);

  useEffect(() => {
    if (!selectedTask?.id) return;
    const nextSelectedTask = allTasksById[selectedTask.id] ?? tasksById[selectedTask.id];
    if (!nextSelectedTask) return;
    if (nextSelectedTask !== selectedTask) {
      setSelectedTask(nextSelectedTask);
    }
  }, [allTasksById, selectedTask, tasksById]);

  useEffect(() => {
    if (!taskFromReadiness?.id) return;
    const nextTaskFromReadiness = appointmentTaskLookupById[taskFromReadiness.id];
    if (!nextTaskFromReadiness) return;
    if (nextTaskFromReadiness !== taskFromReadiness) {
      setTaskFromReadiness(nextTaskFromReadiness);
    }
  }, [appointmentTaskLookupById, taskFromReadiness]);

  useEffect(() => {
    if (selectedAppt?.id && !appointmentsById[selectedAppt.id]) {
      setSelectedAppt(null);
      dispatch(saveCareOrchestrationState({ selectedApptId: null }));
    }
  }, [appointmentsById, dispatch, selectedAppt]);

  useEffect(() => {
    if (!selectedAppt?.id) return;
    const nextSelectedAppt = appointmentsById[selectedAppt.id];
    if (!nextSelectedAppt) return;
    if (nextSelectedAppt !== selectedAppt) {
      setSelectedAppt(nextSelectedAppt);
    }
  }, [selectedAppt, appointmentsById]);

  useEffect(() => {
    if (selectedTask || !persistedState?.selectedTaskId) return;
    const nextSelectedTask =
      allTasksById[persistedState.selectedTaskId] ?? tasksById[persistedState.selectedTaskId];
    if (nextSelectedTask) {
      setSelectedTask(nextSelectedTask);
    }
  }, [allTasksById, persistedState?.selectedTaskId, selectedTask, tasksById]);

  useEffect(() => {
    if (taskFromReadiness || !persistedState?.taskFromReadinessId) return;
    const nextTaskFromReadiness = appointmentTaskLookupById[persistedState.taskFromReadinessId];
    if (nextTaskFromReadiness) {
      setTaskFromReadiness(nextTaskFromReadiness);
    }
  }, [appointmentTaskLookupById, persistedState?.taskFromReadinessId, taskFromReadiness]);

  useEffect(() => {
    if (selectedAppt || !persistedState?.selectedApptId) return;
    const nextSelectedAppt = appointmentsById[persistedState.selectedApptId];
    if (nextSelectedAppt) {
      setSelectedAppt(nextSelectedAppt);
    }
  }, [appointmentsById, persistedState?.selectedApptId, selectedAppt]);

  useEffect(() => {
    const handleFirstInteraction = () => {
      unlockTaskNotificationSound().catch(() => null);

      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };

    window.addEventListener('pointerdown', handleFirstInteraction);
    window.addEventListener('keydown', handleFirstInteraction);

    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  useEffect(() => {
    setBrowserNotificationPermission(getTaskBrowserNotificationPermission());
  }, []);

  useEffect(() => {
    if (!taskNotificationPreferences?.notificationsEnabled) {
      setTaskNotificationToasts([]);
      return;
    }

    const visibleSentNotifications = getVisibleInAppTaskNotifications(
      taskNotifications,
      taskNotificationPreferences,
    ).filter((notification) => notification?.status === NOTIFICATION_STATUS.Sent);

    const nextToastNotifications = visibleSentNotifications.filter(
      (notification) => !shownTaskNotificationToastIdsRef.current.has(notification.id),
    );

    if (!nextToastNotifications.length) return;

    const timeoutIds = nextToastNotifications.map((notification, index) =>
      window.setTimeout(() => {
        shownTaskNotificationToastIdsRef.current.add(notification.id);
        saveToastedTaskNotificationIds(shownTaskNotificationToastIdsRef.current);

        if (taskNotificationPreferences?.sound) {
          playTaskNotificationSound(notification?.category === NOTIFICATION_CATEGORY.Critical);
        }

        setTaskNotificationToasts((prev) => {
          if (prev.some((toast) => toast.id === notification.id)) {
            return prev;
          }

          return [notification, ...prev].slice(0, MAX_TASK_NOTIFICATION_TOASTS);
        });
      }, index * TASK_NOTIFICATION_TOAST_STAGGER_MS),
    );

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [taskNotifications, taskNotificationPreferences]);

  const clearSelections = useCallback(() => {
    setSelectedTask(null);
    setSelectedAppt(null);
    setTaskFromReadiness(null);
    dispatch(
      saveCareOrchestrationState({
        selectedTaskId: null,
        selectedApptId: null,
        taskFromReadinessId: null,
      }),
    );
  }, [dispatch]);

  const patchTaskNotificationPreferences = useCallback(
    async (body, optimisticUpdater) => {
      let previousPreferences = null;

      setTaskNotificationPreferences((prev) => {
        previousPreferences = prev;
        return optimisticUpdater ? optimisticUpdater(prev) : prev;
      });
      setIsTaskNotificationPreferencesSaving(true);

      try {
        const response = await patchMyTaskNotificationPreferences(body);
        setTaskNotificationPreferences(response || previousPreferences);
        return response || previousPreferences;
      } catch (error) {
        if (previousPreferences) {
          setTaskNotificationPreferences(previousPreferences);
        }
        showAlert(
          AlertType.Error,
          '',
          error?.message || 'Failed to update notification preferences',
        );
        return null;
      } finally {
        setIsTaskNotificationPreferencesSaving(false);
      }
    },
    [AlertType.Error, showAlert],
  );

  const sendTaskNotificationTestMessage = useCallback(
    async (body) => {
      try {
        await sendMyTaskNotificationTestMessage(body);

        if (body?.emailAddress) {
          showAlert(AlertType.Success, '', 'Test email notification sent.');
        } else if (body?.slackUserId) {
          showAlert(AlertType.Success, '', 'Test Slack notification sent.');
        }

        return true;
      } catch (error) {
        showAlert(
          AlertType.Error,
          '',
          error?.message || 'Failed to send test notification message',
        );
        return false;
      }
    },
    [AlertType.Error, AlertType.Success, showAlert],
  );

  const searchTaskNotificationSlackUsers = useCallback(
    async (query) => searchMyTaskNotificationSlackUsers(query),
    [],
  );

  const handleRequestBrowserNotificationPermission = useCallback(async () => {
    if (!isBrowserNotificationsSupported()) {
      setBrowserNotificationPermission('denied');
      return 'denied';
    }

    const permission = await requestTaskBrowserNotificationPermission();
    setBrowserNotificationPermission(permission);
    return permission;
  }, []);

  const handleCloseTaskNotificationToast = useCallback((toastId) => {
    setTaskNotificationToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  }, []);

  const applyTaskNotificationStatusLocally = useCallback((notificationId, status) => {
    if (!notificationId) return;

    setTaskNotifications((prev) =>
      prev
        .map((notification) =>
          notification?.id === notificationId ? { ...notification, status } : notification,
        )
        .filter(
          (notification) =>
            notification?.status !== NOTIFICATION_STATUS.Dismissed &&
            notification?.status !== NOTIFICATION_STATUS.Pending,
        ),
    );

    if (status !== NOTIFICATION_STATUS.Sent) {
      setTaskNotificationToasts((prev) => prev.filter((toast) => toast.id !== notificationId));
    }
  }, []);

  const handleDismisstaskNotification = useCallback(
    async (notificationId) => {
      if (!notificationId) return false;

      const previousTaskNotifications = taskNotifications;
      const previousTaskNotificationToasts = taskNotificationToasts;

      applyTaskNotificationStatusLocally(notificationId, NOTIFICATION_STATUS.Dismissed);

      try {
        await patchMyTaskNotificationStatus(notificationId, NOTIFICATION_STATUS.Dismissed);
        return true;
      } catch (error) {
        setTaskNotifications(previousTaskNotifications);
        setTaskNotificationToasts(previousTaskNotificationToasts);
        showAlert(AlertType.Error, '', error?.message || 'Failed to dismiss task notification');
        return false;
      }
    },
    [
      AlertType.Error,
      applyTaskNotificationStatusLocally,
      showAlert,
      taskNotificationToasts,
      taskNotifications,
    ],
  );

  const handleOpenTaskNotification = useCallback(
    async (notification) => {
      const taskId = notification?.taskId;
      const patientId = notification?.patientId;
      const shouldOpenWithoutStatusFilter =
        notification?.category === NOTIFICATION_CATEGORY.SubtaskCanceled ||
        notification?.category === NOTIFICATION_CATEGORY.SubtasksCompleted;

      if (!taskId || patientId == null) return false;

      const previousTaskNotifications = taskNotifications;
      const previousTaskNotificationToasts = taskNotificationToasts;

      applyTaskNotificationStatusLocally(notification.id, NOTIFICATION_STATUS.Read);

      try {
        await patchMyTaskNotificationStatus(notification.id, NOTIFICATION_STATUS.Read);

        if (shouldOpenWithoutStatusFilter) {
          const fetchedTask = await fetchTaskQueueItemById(taskId);
          if (fetchedTask?.id) {
            upsertTaskFromResponse(fetchedTask);
          }
        }

        navigate(
          getCareOrchestrationPath(TAB_KEYS.TASKS, {
            statusFilter: shouldOpenWithoutStatusFilter ? null : 'open',
            patientId: `${patientId}`,
            taskId: `${taskId}`,
          }),
        );

        return true;
      } catch (error) {
        setTaskNotifications(previousTaskNotifications);
        setTaskNotificationToasts(previousTaskNotificationToasts);
        showAlert(AlertType.Error, '', error?.message || 'Failed to open task notification');
        return false;
      }
    },
    [
      AlertType.Error,
      NOTIFICATION_CATEGORY.SubtaskCanceled,
      NOTIFICATION_CATEGORY.SubtasksCompleted,
      applyTaskNotificationStatusLocally,
      fetchTaskQueueItemById,
      navigate,
      showAlert,
      taskNotificationToasts,
      taskNotifications,
      upsertTaskFromResponse,
    ],
  );

  const handleMarkAllTaskNotificationsRead = useCallback(async () => {
    const unreadNotificationIds = taskNotifications
      .filter((notification) => notification?.status === NOTIFICATION_STATUS.Sent)
      .map((notification) => notification.id);

    if (!unreadNotificationIds.length) return true;

    const unreadNotificationIdSet = new Set(unreadNotificationIds);
    const previousTaskNotifications = taskNotifications;
    const previousTaskNotificationToasts = taskNotificationToasts;

    setTaskNotifications((prev) =>
      prev.map((notification) =>
        unreadNotificationIdSet.has(notification?.id)
          ? { ...notification, status: NOTIFICATION_STATUS.Read }
          : notification,
      ),
    );
    setTaskNotificationToasts((prev) =>
      prev.filter((toast) => !unreadNotificationIdSet.has(toast.id)),
    );

    try {
      await markAllMyTaskNotificationsRead();
      return true;
    } catch (error) {
      setTaskNotifications(previousTaskNotifications);
      setTaskNotificationToasts(previousTaskNotificationToasts);
      showAlert(
        AlertType.Error,
        '',
        error?.message || 'Failed to mark all task notifications read',
      );
      return false;
    }
  }, [AlertType.Error, showAlert, taskNotificationToasts, taskNotifications]);

  useEffect(() => {
    if (!taskNotificationPreferences?.notificationsEnabled) return;
    if (!isTaskNotificationChannelEnabled(taskNotificationPreferences, 'browser')) return;
    if (browserNotificationPermission !== 'granted') return;

    const visibleSentNotifications = getVisibleBrowserTaskNotifications(
      taskNotifications,
      taskNotificationPreferences,
    ).filter((notification) => notification?.status === NOTIFICATION_STATUS.Sent);

    const nextBrowserNotifications = visibleSentNotifications.filter(
      (notification) => !shownBrowserNotificationIdsRef.current.has(notification.id),
    );

    if (!nextBrowserNotifications.length) return;

    nextBrowserNotifications.forEach((notification) => {
      shownBrowserNotificationIdsRef.current.add(notification.id);

      showTaskBrowserNotification(notification, {
        onClick: async () => {
          await handleOpenTaskNotification(notification);
        },
      });
    });
  }, [
    browserNotificationPermission,
    handleOpenTaskNotification,
    taskNotificationPreferences,
    taskNotifications,
  ]);

  const handleOpenTaskNotificationToast = useCallback(
    async (notification) => {
      setTaskNotificationToasts((prev) => prev.filter((toast) => toast.id !== notification.id));
      await handleOpenTaskNotification(notification);
    },
    [handleOpenTaskNotification],
  );

  const handleSetPatientSearch = useCallback(
    async (nextPatientSearch) => {
      const patientMrn = nextPatientSearch?.mrn ?? nextPatientSearch?.id ?? null;

      if (
        nextPatientSearch?.kind === 'patient' &&
        nextPatientSearch?.id != null &&
        patientMrn != null
      ) {
        setIsExpandingTaskSearch(true);

        try {
          const accumulatedPatientTaskQueueRows = [];
          let patientTaskQueueMeta = null;
          let offset = 0;
          let shouldContinue = true;

          /* eslint-disable no-await-in-loop */
          while (shouldContinue) {
            const {
              taskQueueRows: nextPatientTaskQueueRows,
              taskQueueMeta: nextPatientTaskQueueMeta,
            } = await fetchTaskQueueData({
              pageSize: TASK_QUEUE_PAGE_SIZE,
              offset,
              sortMode: taskSortModeRef.current,
              team: activeTeamRef.current,
              ownerScope: ownerFilterRef.current,
              statusScope: statusFilterRef.current,
              mrn: patientMrn,
            });

            patientTaskQueueMeta = nextPatientTaskQueueMeta;
            accumulatedPatientTaskQueueRows.push(...nextPatientTaskQueueRows);

            const total = nextPatientTaskQueueMeta?.total ?? accumulatedPatientTaskQueueRows.length;

            if (
              !nextPatientTaskQueueRows.length ||
              accumulatedPatientTaskQueueRows.length >= total
            ) {
              shouldContinue = false;
              continue;
            }

            offset += TASK_QUEUE_PAGE_SIZE;
          }
          /* eslint-enable no-await-in-loop */

          if (accumulatedPatientTaskQueueRows.length) {
            syncTaskQueueState({
              nextTaskQueueRows: mergeTaskRowsById(
                taskQueueRowsRef.current,
                accumulatedPatientTaskQueueRows,
              ),
              nextTaskQueueMeta: patientTaskQueueMeta ?? taskQueueMetaRef.current,
              hydrateSubtasks: true,
            });
          }
        } catch (error) {
          showAlert(
            AlertType.Error,
            '',
            error?.message || 'Unable to load tasks for the selected patient.',
          );
        } finally {
          setIsExpandingTaskSearch(false);
        }
      }

      setPatientSearch(nextPatientSearch);
      dispatch(saveCareOrchestrationState({ patientSearch: nextPatientSearch }));
    },
    [AlertType.Error, dispatch, showAlert, syncTaskQueueState],
  );

  const handleSearchTaskPatients = useCallback(async (query) => {
    const trimmedQuery = `${query || ''}`.trim();
    if (!trimmedQuery) return [];

    const { rows } = await fetchTaskSearchPatients({
      query: trimmedQuery,
      team: activeTeamRef.current,
      ownerScope: ownerFilterRef.current,
      statusScope: statusFilterRef.current,
    });

    return rows.map((row) => {
      const patient = row?.patient || {};
      const taskSummary = row?.taskSummary || {};

      return {
        key: `patient-${patient?.id ?? patient?.mrn ?? ''}`,
        label:
          `${patient?.preferredName || patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
        count: taskSummary?.count ?? 0,
        value: {
          kind: 'patient',
          ...patient,
          taskSummary,
        },
      };
    });
  }, []);

  const handleClearTaskSearch = useCallback(async () => {
    handleSetPatientSearch(null);
    await resetTaskSearchScope();
  }, [handleSetPatientSearch, resetTaskSearchScope]);

  const expandPatientGroup = useCallback(
    (patientGroupKey) => {
      if (!patientGroupKey || expandedPatients[patientGroupKey]) return;

      const nextExpandedPatients = {
        ...expandedPatients,
        [patientGroupKey]: true,
      };

      setExpandedPatients(nextExpandedPatients);
      dispatch(saveCareOrchestrationState({ expandedPatients: nextExpandedPatients }));
    },
    [dispatch, expandedPatients],
  );

  useEffect(() => {
    if (!hasEnabledTabs) {
      if (
        selectedTask ||
        selectedAppt ||
        taskFromReadiness ||
        persistedState?.selectedTaskId ||
        persistedState?.selectedApptId ||
        persistedState?.taskFromReadinessId
      ) {
        clearSelections();
      }
      if (location.pathname !== CARE_ORCHESTRATION_BASE_PATH) {
        navigate(CARE_ORCHESTRATION_BASE_PATH, { replace: true });
      }
      return;
    }

    const canonicalPath = getCareOrchestrationPath(
      view,
      view === TAB_KEYS.TASKS && (requestedRoute.statusFilter || requestedRoute.taskId)
        ? requestedRoute
        : null,
    );
    if (location.pathname !== canonicalPath) {
      if (requestedView && requestedView !== view) {
        clearSelections();
      }
      navigate(canonicalPath, { replace: true });
    }
  }, [
    clearSelections,
    hasEnabledTabs,
    location.pathname,
    navigate,
    persistedState?.selectedApptId,
    persistedState?.selectedTaskId,
    persistedState?.taskFromReadinessId,
    requestedView,
    selectedAppt,
    selectedTask,
    taskFromReadiness,
    view,
  ]);

  useEffect(() => {
    loadTaskNotifications();
  }, [loadTaskNotifications]);

  useInterval(() => {
    if (isStreamSSEEnabled) return;
    if (document.visibilityState !== 'visible') return;

    loadTaskNotifications();
  }, 60 * 1000);

  useEffect(() => {
    if (document.visibilityState === 'visible' && isStreamSSEEnabled) {
      openTaskNotificationsStream();
    }

    return () => {
      closeTaskNotificationsStream();
    };
  }, [closeTaskNotificationsStream, isStreamSSEEnabled, openTaskNotificationsStream]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' || !isStreamSSEEnabled) {
        closeTaskNotificationsStream();
        return;
      }

      if (
        document.visibilityState === 'visible' &&
        isStreamSSEEnabled &&
        !taskNotificationsStreamRef.current
      ) {
        openTaskNotificationsStream();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [closeTaskNotificationsStream, isStreamSSEEnabled, openTaskNotificationsStream]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsTaskNotificationPreferencesLoading(true);

      try {
        const response = await getMyTaskNotificationPreferences();

        if (cancelled) return;

        setTaskNotificationPreferences(response || DEFAULT_NOTIFICATION_PREFERENCES);
      } catch (error) {
        if (cancelled) return;

        setTaskNotificationPreferences(DEFAULT_NOTIFICATION_PREFERENCES);
        showAlert(AlertType.Error, '', error?.message || 'Failed to load notification preferences');
      } finally {
        if (!cancelled) {
          setIsTaskNotificationPreferencesLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (view !== TAB_KEYS.TASKS || (!requestedRoute.statusFilter && !requestedRoute.taskId)) return;

    const routeKey = `${location.key}:${location.pathname}`;
    if (appliedTaskRouteKeyRef.current === routeKey) return;

    if (ownerFilter !== 'all') {
      setOwnerFilter('all');
      dispatch(saveCareOrchestrationState({ ownerFilter: 'all' }));
    }

    if (activeTeam !== 'all') {
      setActiveTeam('all');
      dispatch(saveCareOrchestrationState({ activeTeam: 'all' }));
    }

    const nextStatusFilter = requestedRoute.statusFilter;
    if (nextStatusFilter && statusFilter !== nextStatusFilter) {
      setStatusFilter(nextStatusFilter);
      dispatch(saveCareOrchestrationState({ statusFilter: nextStatusFilter }));
    }

    if (requestedRoute.patientId) {
      expandPatientGroup(`patient-${requestedRoute.patientId}`);
    }

    if (!requestedRoute.taskId) {
      appliedTaskRouteKeyRef.current = routeKey;
      return;
    }

    if (isCareOrchestrationLoading) return;

    const requestedTask = allTasksById[requestedRoute.taskId] ?? tasksById[requestedRoute.taskId];
    const taskMatchesRoute =
      !!requestedTask &&
      (!requestedRoute.patientId ||
        `${getTaskPatientId(requestedTask) ?? ''}` === requestedRoute.patientId) &&
      (!requestedRoute.statusFilter ||
        matchesTaskStatusFilter(requestedTask, requestedRoute.statusFilter));

    if (taskMatchesRoute) {
      const requestedTaskPatient = {
        id: getTaskPatientId(requestedTask),
        firstName: requestedTask?.patient?.firstName || '',
        lastName: requestedTask?.patient?.lastName || '',
        preferredName: requestedTask?.patient?.preferredName || '',
        dob: requestedTask?.patient?.dob || null,
        mrn: requestedTask?.patient?.mrn || null,
        diabetesStatus: requestedTask?.patient?.diabetesStatus || null,
        cognitiveStatus: requestedTask?.patient?.cognitiveStatus || null,
      };

      if (`${patientSearch?.id ?? ''}` !== `${requestedTask.patientId}`) {
        handleSetPatientSearch(requestedTaskPatient);
      }

      expandPatientGroup(getTaskPatientGroupKey(requestedTask));

      if (selectedTask?.id !== requestedTask.id) {
        setSelectedTask(requestedTask);
        dispatch(saveCareOrchestrationState({ selectedTaskId: requestedTask.id }));
      }
      appliedTaskRouteKeyRef.current = routeKey;
      return;
    }

    if (!requestedTask) {
      if (hasMoreTasks && !isLoadingMoreTasks) {
        loadMoreTaskQueueData().catch(() => null);
      }
      return;
    }

    if (selectedTask || persistedState?.selectedTaskId) {
      setSelectedTask(null);
      dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
    }

    appliedTaskRouteKeyRef.current = routeKey;
  }, [
    activeTeam,
    dispatch,
    expandPatientGroup,
    isCareOrchestrationLoading,
    location.key,
    location.pathname,
    ownerFilter,
    patientSearch?.id,
    persistedState?.selectedTaskId,
    requestedRoute.patientId,
    requestedRoute.statusFilter,
    requestedRoute.taskId,
    selectedTask,
    statusFilter,
    allTasksById,
    tasksById,
    hasMoreTasks,
    isLoadingMoreTasks,
    loadMoreTaskQueueData,
    view,
  ]);

  useEffect(() => {
    if (!view) return;
    dispatch(saveCareOrchestrationView(view));
  }, [dispatch, view]);

  const handleChangeView = (nextView) => {
    if (!nextView || nextView === view) return;
    navigate(getCareOrchestrationPath(nextView));
  };

  const handleResetTaskRoute = () => {
    if (view !== TAB_KEYS.TASKS) return;

    const baseTasksPath = getCareOrchestrationPath(TAB_KEYS.TASKS);
    if (location.pathname === baseTasksPath) return;

    navigate(baseTasksPath, { replace: true });
  };

  const handleSetSelectedTask = (task) => {
    handleResetTaskRoute();
    setSelectedTask(task);
    dispatch(saveCareOrchestrationState({ selectedTaskId: task?.id || null }));
  };

  const handleSetOwnerFilter = (nextOwnerFilter) => {
    handleResetTaskRoute();
    setOwnerFilter(nextOwnerFilter);
    dispatch(saveCareOrchestrationState({ ownerFilter: nextOwnerFilter }));
  };

  const handleSetActiveTeam = (nextActiveTeam) => {
    handleResetTaskRoute();
    setActiveTeam(nextActiveTeam);
    dispatch(saveCareOrchestrationState({ activeTeam: nextActiveTeam }));
  };

  const handleSetStatusFilter = (nextStatusFilter) => {
    handleResetTaskRoute();
    setStatusFilter(nextStatusFilter);
    dispatch(saveCareOrchestrationState({ statusFilter: nextStatusFilter }));
  };

  const handleSetTaskSortMode = (nextTaskSortMode) => {
    const normalizedTaskSortMode = Number(nextTaskSortMode);
    if (!Number.isInteger(normalizedTaskSortMode) || normalizedTaskSortMode === taskSortMode)
      return;

    handleResetTaskRoute();
    setTaskSortMode(normalizedTaskSortMode);
    setPatientSearch(null);
    setExpandedPatients({});
    dispatch(
      saveCareOrchestrationState({
        patientSearch: null,
        taskSortMode: normalizedTaskSortMode,
        expandedPatients: {},
      }),
    );
  };

  const handleSort = (nextKey) => {
    handleResetTaskRoute();
    if (nextKey === sortKey) {
      const nextSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(nextSortDirection);
      dispatch(saveCareOrchestrationState({ sortDirection: nextSortDirection }));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
    dispatch(
      saveCareOrchestrationState({
        sortKey: nextKey,
        sortDirection: 'asc',
      }),
    );
  };

  const handleSetSelectedAppt = (appointment) => {
    setSelectedAppt(appointment);
    dispatch(saveCareOrchestrationState({ selectedApptId: appointment?.id || null }));
  };

  const handleSetTaskFromReadiness = (task) => {
    setTaskFromReadiness(task);
    dispatch(saveCareOrchestrationState({ taskFromReadinessId: task?.id || null }));
  };

  const handleSetExpandedPatients = (nextExpandedPatients) => {
    handleResetTaskRoute();
    const resolvedExpandedPatients =
      typeof nextExpandedPatients === 'function'
        ? nextExpandedPatients(expandedPatients)
        : nextExpandedPatients;

    setExpandedPatients(resolvedExpandedPatients);
    dispatch(saveCareOrchestrationState({ expandedPatients: resolvedExpandedPatients }));
  };

  const readinessStats = useMemo(
    () =>
      sortedAppts.reduce(
        (acc, appointment) => {
          const readinessStatus = getAppointmentReadinessStatus(
            appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
          );

          if (readinessStatus === 'green') acc.green += 1;
          if (readinessStatus === 'yellow') acc.yellow += 1;
          if (readinessStatus === 'red') acc.red += 1;

          return acc;
        },
        { red: 0, yellow: 0, green: 0 },
      ),
    [appointmentTasksById, sortedAppts],
  );

  const patientScopedTasks = useMemo(() => {
    if (!patientSearch) return availableTasks;

    if (patientSearch.kind === 'reason') {
      return availableTasks.filter(
        (task) => Number(task?.reasonId) === Number(patientSearch.reasonId),
      );
    }

    if (patientSearch.kind === 'priority') {
      return availableTasks.filter((task) => `${task?.priority || ''}` === patientSearch.priority);
    }

    if (patientSearch.id == null) return availableTasks;

    return availableTasks.filter(
      (task) => `${getTaskPatientId(task) ?? ''}` === `${patientSearch.id}`,
    );
  }, [availableTasks, patientSearch]);

  const taskQueueTotals = useMemo(
    () => taskQueueMeta || careOrchestrationMeta?.tasks || null,
    [careOrchestrationMeta?.tasks, taskQueueMeta],
  );

  const filtered = useMemo(() => {
    const passesNonTeamFilters = (task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      const isUnassigned = !ownerLabel || ownerLabel === 'unassigned';
      const isMyTask = !!currentUserNameLower && ownerLabel === currentUserNameLower;
      const isFollowingTask = isTaskFollowedByCurrentUser(task, currentUserId);
      const isSessionCompletedTask = !!sessionCompletedTaskIds[task.id];

      if (ownerFilter === 'my' && !isMyTask) return false;
      if (ownerFilter === 'following' && !isFollowingTask) return false;
      if (ownerFilter === 'unassigned' && !isUnassigned) return false;
      if (!isSessionCompletedTask && !matchesTaskStatusFilter(task, statusFilter)) return false;

      return true;
    };

    const nextFilteredTasks = patientScopedTasks.filter((task) => {
      if (!passesNonTeamFilters(task)) return false;
      if (activeTeam !== 'all' && task.team !== activeTeam) return false;
      return true;
    });

    const filteredIdSet = new Set(nextFilteredTasks.map((t) => t.id));

    patientScopedTasks.forEach((task) => {
      if (filteredIdSet.has(task.id)) return;
      const parentTaskId = `${task.parentTaskId || ''}`.trim();
      if (parentTaskId && filteredIdSet.has(parentTaskId)) {
        nextFilteredTasks.push(task);
        filteredIdSet.add(task.id);
      }
    });

    patientScopedTasks.forEach((task) => {
      if (filteredIdSet.has(task.id)) return;
      const parentTaskId = `${task.parentTaskId || ''}`.trim();
      if (!parentTaskId) {
        const hasMatchingSubtask = patientScopedTasks.some(
          (sub) => `${sub.parentTaskId || ''}`.trim() === task.id && filteredIdSet.has(sub.id),
        );
        if (hasMatchingSubtask) {
          nextFilteredTasks.push(task);
          filteredIdSet.add(task.id);
        }
      }
    });

    patientScopedTasks.forEach((task) => {
      if (filteredIdSet.has(task.id)) return;
      const parentTaskId = `${task.parentTaskId || ''}`.trim();
      if (parentTaskId && filteredIdSet.has(parentTaskId)) {
        nextFilteredTasks.push(task);
        filteredIdSet.add(task.id);
      }
    });

    if (taskSortMode === TASK_QUEUE_SORT_MODE.PatientPriority && !patientSearch) {
      return nextFilteredTasks;
    }

    return nextFilteredTasks.sort((a, b) => {
      const priority = {
        [TASK_PRIORITY.Critical]: 0,
        [TASK_PRIORITY.High]: 1,
        [TASK_PRIORITY.Medium]: 2,
        [TASK_PRIORITY.Low]: 3,
      };
      const statusOrder = {
        pending: 0,
        'in-progress': 1,
        [TASK_DISPLAY_STATUS.Done]: 2,
      };

      const dir = sortDirection === 'asc' ? 1 : -1;
      let primary = 0;

      if (sortKey === 'status') {
        primary =
          (statusOrder[getTaskDisplayStatus(a)] ?? 99) -
          (statusOrder[getTaskDisplayStatus(b)] ?? 99);
      } else if (sortKey === 'priority') {
        primary = (priority[a.priority] ?? 99) - (priority[b.priority] ?? 99);
      } else {
        const aDue = a.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
        const bDue = b.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
        primary = aDue - bDue;
      }

      if (primary !== 0) return dir * primary;

      const aDue = a.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
      const bDue = b.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
      return aDue - bDue || priority[a.priority] - priority[b.priority];
    });
  }, [
    activeTeam,
    currentUserId,
    currentUserNameLower,
    ownerFilter,
    patientSearch,
    patientScopedTasks,
    sessionCompletedTaskIds,
    sortDirection,
    sortKey,
    statusFilter,
    taskSortMode,
  ]);

  useEffect(() => {
    if (!selectedTask) return;
    if (selectedTask?.parentTaskId) return;

    const hasSelectedTask = filtered.some((task) => task.id === selectedTask.id);
    if (hasSelectedTask) return;

    setSelectedTask(null);
    dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
  }, [dispatch, filtered, selectedTask]);

  const ownerStats = useMemo(() => {
    if (!patientSearch && taskQueueTotals) {
      return {
        all: taskQueueTotals.totalAll ?? 0,
        my: taskQueueTotals.totalMyTasks ?? 0,
        following: taskQueueTotals.totalFollowing ?? 0,
        unassigned: taskQueueTotals.totalUnassigned ?? 0,
      };
    }

    if (!patientScopedTasks.length) return DEFAULT_OWNER_STATS;

    const baseTasks = patientScopedTasks.filter((task) => {
      if (activeTeam !== 'all' && task.team !== activeTeam) return false;
      return matchesTaskStatusFilter(task, statusFilter);
    });

    const my = baseTasks.filter((task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      return !!currentUserNameLower && ownerLabel === currentUserNameLower;
    }).length;

    const unassigned = baseTasks.filter((task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      return !ownerLabel || ownerLabel === 'unassigned';
    }).length;

    const following = baseTasks.filter((task) =>
      isTaskFollowedByCurrentUser(task, currentUserId),
    ).length;

    return {
      all: baseTasks.length,
      my,
      following,
      unassigned,
    };
  }, [
    activeTeam,
    currentUserId,
    currentUserNameLower,
    patientScopedTasks,
    patientSearch,
    statusFilter,
    taskQueueTotals,
  ]);

  const stats = useMemo(() => {
    if (!patientSearch && taskQueueTotals) {
      return {
        total: taskQueueTotals.totalUncompleted ?? taskQueueTotals.total ?? 0,
        member: taskQueueTotals.totalMember ?? 0,
        ops: taskQueueTotals.totalOps ?? 0,
        clinical: taskQueueTotals.totalClinical ?? 0,
        rcm: taskQueueTotals.totalRcm ?? 0,
        cognitive: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'cognitive' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        kisunla: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'kisunla' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-iv': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-iv' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-sc': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-sc' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        critical: taskQueueTotals.totalCritical ?? 0,
        blocked: taskQueueTotals.totalBlocked ?? 0,
        escalated: taskQueueTotals.totalEscalated ?? 0,
        messages: taskQueueTotals.totalMessages ?? 0,
        assessments: taskQueueTotals.totalAssessments ?? 0,
      };
    }

    if (patientScopedTasks.length) {
      return {
        total: patientScopedTasks.filter(
          (task) => getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        member: patientScopedTasks.filter(
          (task) =>
            task.team === 'member' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        ops: patientScopedTasks.filter(
          (task) => task.team === 'ops' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        clinical: patientScopedTasks.filter(
          (task) =>
            task.team === 'clinical' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        rcm: patientScopedTasks.filter(
          (task) => task.team === 'rcm' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        cognitive: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'cognitive' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        kisunla: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'kisunla' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-iv': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-iv' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-sc': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-sc' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        critical: patientScopedTasks.filter(
          (task) =>
            task.priority === TASK_PRIORITY.Critical &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        blocked: patientScopedTasks.filter((task) => isTaskBlockedStatus(task)).length,
        escalated: patientScopedTasks.filter((task) => isTaskEscalatedStatus(task)).length,
        messages: patientScopedTasks.filter(
          (task) => isMessageTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        assessments: patientScopedTasks.filter(
          (task) =>
            isAssessmentTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
      };
    }

    return DEFAULT_STATS;
  }, [patientScopedTasks, patientSearch, taskQueueTotals]);

  const totalTaskCount = useMemo(() => {
    if (!patientSearch && taskQueueTotals) {
      return taskQueueTotals.totalUncompleted ?? taskQueueTotals.total ?? filtered.length;
    }

    return filtered.length;
  }, [filtered.length, patientSearch, taskQueueTotals]);

  return (
    <div className={styles.root}>
      <CareOrchestrationHeader
        view={view}
        readinessStats={readinessStats}
        stats={stats}
        tabAvailability={{
          [TAB_KEYS.READINESS]: isReadinessEnabled,
          [TAB_KEYS.TASKS]: isTasksEnabled,
        }}
        onChangeView={(nextView) => {
          const isNextViewEnabled = availableTabs.some(
            (tab) => tab.key === nextView && tab.enabled,
          );
          if (!isNextViewEnabled) return;
          handleChangeView(nextView);
        }}
        onClearSelections={clearSelections}
        taskNotificationPreferences={taskNotificationPreferences}
        isTaskNotificationPreferencesLoading={isTaskNotificationPreferencesLoading}
        isTaskNotificationPreferencesSaving={isTaskNotificationPreferencesSaving}
        onPatchTaskNotificationPreferences={patchTaskNotificationPreferences}
        onSearchTaskNotificationSlackUsers={searchTaskNotificationSlackUsers}
        onSendTaskNotificationTestMessage={sendTaskNotificationTestMessage}
        taskNotifications={taskNotifications}
        isTaskNotificationsLoading={isTaskNotificationsLoading}
        onDismissTaskNotification={handleDismisstaskNotification}
        onOpenTaskNotification={handleOpenTaskNotification}
        onMarkAllTaskNotificationsRead={handleMarkAllTaskNotificationsRead}
        browserNotificationPermission={browserNotificationPermission}
        onRequestBrowserNotificationPermission={handleRequestBrowserNotificationPermission}
      />

      <div className={styles.content}>
        <div className={styles.contentInner}>
          {!hasEnabledTabs && (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateTitle}>Care Orchestration is coming soon</div>
              <div className={styles.emptyStateText}>
                Tasks and Readiness will appear here once those feature flags are enabled.
              </div>
            </div>
          )}

          {hasEnabledTabs && view === TAB_KEYS.READINESS && (
            <ReadinessView
              isLoading={isCareOrchestrationLoading}
              apptsByDate={apptsByDate}
              readinessStats={readinessStats}
              selectedAppt={selectedAppt}
              taskFromReadiness={taskFromReadiness}
              appointmentTasksById={appointmentTasksById}
              getAppointmentTaskLookupKey={getAppointmentTaskLookupKey}
              setSelectedAppt={handleSetSelectedAppt}
              setTaskFromReadiness={handleSetTaskFromReadiness}
              onMarkComplete={handleMarkComplete}
              markingTaskId={markingTaskId}
              onReassignTask={handleReassignTask}
              reassigningTaskId={reassigningTaskId}
              onFollowTask={handleFollowTask}
              followingTaskId={followingTaskId}
              onUnfollowTask={handleUnfollowTask}
              unfollowingTaskId={unfollowingTaskId}
              onAssignToMe={handleAssignToMe}
              assigningTaskId={assigningTaskIds}
              onUnassignTask={handleUnassignTask}
              unassigningTaskId={unassigningTaskId}
              onBlockTask={handleBlockTask}
              blockingTaskId={blockingTaskId}
              onUnblockTask={handleUnblockTask}
              unblockingTaskId={unblockingTaskId}
              onEscalateTask={handleEscalateTask}
              escalatingTaskId={escalatingTaskId}
              onDeescalateTask={handleDeescalateTask}
              deescalatingTaskId={deescalatingTaskId}
              onDelegateSubtasks={handleDelegateSubtasks}
              delegatingSubtasksTaskId={delegatingSubtasksTaskId}
              onCancelSubtask={handleCancelSubtask}
              cancelingSubtaskId={cancelingSubtaskId}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              onReplySent={refreshCareOrchestrationData}
              delegatedSubtasksByParentId={delegatedSubtasksByParentId}
            />
          )}

          {hasEnabledTabs && view === TAB_KEYS.TASKS && (
            <TasksView
              isLoading={isCareOrchestrationLoading}
              ownerFilter={ownerFilter}
              setOwnerFilter={handleSetOwnerFilter}
              ownerStats={ownerStats}
              currentUserNameLower={currentUserNameLower}
              activeTeam={activeTeam}
              setActiveTeam={handleSetActiveTeam}
              statusFilter={statusFilter}
              setStatusFilter={handleSetStatusFilter}
              patientSearch={patientSearch}
              setPatientSearch={handleSetPatientSearch}
              stats={stats}
              filtered={filtered}
              totalTaskCount={totalTaskCount}
              taskQueueTotals={taskQueueTotals}
              searchableTasks={availableTasks}
              taskSortMode={taskSortMode}
              setTaskSortMode={handleSetTaskSortMode}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              isExpandingTaskSearch={isExpandingTaskSearch}
              onExpandTaskSearchScope={expandTaskSearchScope}
              onSearchTaskPatients={handleSearchTaskPatients}
              onClearTaskSearch={handleClearTaskSearch}
              hasMoreTasks={!patientSearch && hasMoreTasks}
              isLoadingMoreTasks={isLoadingMoreTasks}
              onLoadMoreTasks={loadMoreTaskQueueData}
              selectedTask={selectedTask}
              setSelectedTask={handleSetSelectedTask}
              expandedPatients={expandedPatients}
              setExpandedPatients={handleSetExpandedPatients}
              onAssignToMe={handleAssignToMe}
              assigningTaskId={assigningTaskIds}
              onUnassignTask={handleUnassignTask}
              unassigningTaskId={unassigningTaskId}
              onMarkComplete={handleMarkComplete}
              markingTaskId={markingTaskId}
              onReassignTask={handleReassignTask}
              reassigningTaskId={reassigningTaskId}
              onFollowTask={handleFollowTask}
              followingTaskId={followingTaskId}
              onUnfollowTask={handleUnfollowTask}
              unfollowingTaskId={unfollowingTaskId}
              onBlockTask={handleBlockTask}
              blockingTaskId={blockingTaskId}
              onUnblockTask={handleUnblockTask}
              unblockingTaskId={unblockingTaskId}
              onEscalateTask={handleEscalateTask}
              escalatingTaskId={escalatingTaskId}
              onDeescalateTask={handleDeescalateTask}
              deescalatingTaskId={deescalatingTaskId}
              onDelegateSubtasks={handleDelegateSubtasks}
              delegatingSubtasksTaskId={delegatingSubtasksTaskId}
              onCancelSubtask={handleCancelSubtask}
              cancelingSubtaskId={cancelingSubtaskId}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              onReplySent={refreshCareOrchestrationData}
              delegatedSubtasksByParentId={delegatedSubtasksByParentId}
            />
          )}
        </div>
      </div>
      <TaskNotificationToasts
        toasts={taskNotificationToasts}
        onClose={handleCloseTaskNotificationToast}
        onOpen={handleOpenTaskNotificationToast}
      />
    </div>
  );
}
